// Quote Builder — left panel, "PRODUCTS" tab.
//
// Live catalogue search per supplier tab.
//
// Endpoints (all live; see kitchenos-api server.js):
//   • TKC    → /api/catalogue/search?type=supplier&id=tkc&q=
//             (moved off /api/tkc/search 2026-05-24 — TKC products now live
//             in the PG-backed supplier catalogue. Door range cards + door
//             picker similarly repointed at /api/catalogue/door-ranges and
//             /api/catalogue/doors/sizes.)
//   • DTN    → /api/dtn/search?q=&limit=     (Trade Kitchens by DTN)
//   • LDL    → /api/ldl/search?q=&limit=
//   • CUSTOM → /api/catalogue/search?q=&type=supplier  ("My Catalogue")
//
// The unified result shape (`SearchHit`) is what the UI renders. TKC and
// CUSTOM both use `normaliseCustom` because they share the catalogue wire
// shape; DTN and LDL keep their own normalisers until their data also
// moves into the PG catalogue.

import { api } from "@tkos/api-client";
import { confirmDialog } from "@tkos/shared-ui";
import type { QuoteStore } from "./state";
import type { ItemCategory, QuoteItem, SupplierDef, TkcDisplayCategory } from "./types";
import {
  ITEM_CATEGORIES,
  SUPPLIERS,
  TKC_CATEGORY_ORDER,
  TKC_CATEGORY_SEARCH_TERMS,
  TKC_CATEGORY_TO_ITEM,
} from "./types";
import { escapeHtml, newId } from "./formatters";
import {
  classifyCatalogueHit,
  composeCatalogueDesc,
  tkcInferCategory,
  type CatalogueRowFacts,
} from "./catalogue-classify";
import { loadDiscountsFor, resolveDiscPct } from "./discounts";
import { supplierLabelForSlug } from "./supplier-label";
import { fuzzyFilter } from "./fuzzy";

import { getSjbDefaults, loadSjbCategories, type SjbCategory,
         processingUnitLabel } from "./sjb-catalogue";
import { mountSjbDefaults } from "./sjb-defaults-strip";
import { recalcItem } from "./pricing";

/**
 * Hand off from browsing to configuring.
 *
 * Was `document.querySelector("[data-role='picker-area']").scrollIntoView(...)`
 * in four places — correct while the picker lived in the right-hand column and
 * opening one meant scrolling the quote to it. Since the pickers moved into
 * the left panel (2026-09-05) that would scroll the wrong pane: the picker is
 * now a sibling of this panel, not a distant section of the other one.
 *
 * index.ts owns the browse/configure switch and drives it off picker-selection,
 * so there is nothing to do here but put the panel back at the top — the
 * retailer is about to read a configurator from its first field, not resume
 * wherever the results list was scrolled to.
 */
function handOffToPicker(): void {
  const pane = document.querySelector<HTMLElement>("[data-role='picker-area']");
  if (pane) pane.scrollTop = 0;
}

/* ── Shared left-panel result ranking ───────────────────────────────────
 *
 * Every LH tab funnels through ONE runSearch() call site, so a re-rank
 * inserted there covers all of them. It is opt-in per tab, and deliberately
 * NOT switched on for everything:
 *
 *   SJB      ON.  516 rows fetched at limit 200 — the response is
 *                 effectively the whole catalogue, so this is a true
 *                 re-rank rather than a reordering of an arbitrary window.
 *                 This is where the entire win is.
 *   DTN/LDL  OFF. dtn_search.js and ldl_search.js already score server-side
 *                 using signals this pass cannot see: +50 when a token
 *                 matches the cabinet's WIDTH, an in-stock boost, and 15
 *                 trade aliases (hl -> highline, mw -> microwave).
 *                 Re-ranking their top 50 here can only lose that.
 *   TKC/PWS  OFF. 26,285 rows behind a 50-row window, and a re-rank can only
 *                 reorder what the fetch returned (the same constraint
 *                 add-item-search.ts documents). The server's AND-match
 *                 returns NOTHING for a typo, so there is nothing to rescue.
 *                 Fixing recall there needs pg_trgm — deliberately skipped,
 *                 it touches an endpoint the RH box, the supplier portal and
 *                 the admin viewer all share.
 */
const RERANK_TABS: ReadonlySet<string> = new Set(["SJB"]);

/** Threshold 120, not fuzzyFilter's default 30 — the same reasoning as the
 *  TKC range cards. A subsequence match scores from 60, so a looser bar turns
 *  three stray letters into a confident suggestion. 130 is the lev1 band, so
 *  120 admits a one-character typo and rejects letter soup. */
const RERANK_THRESHOLD = 120;

/** Everything about a hit worth matching on.
 *
 *  A flat code+desc match ignores most of what a retailer actually types.
 *  `meta` is where each tab stashes the rest: the door range and colour for
 *  catalogue suppliers, the brand for LDL, and — since the 2026-09-05 recipe
 *  import — SJB's browsing categories, which is the single field most likely
 *  to be typed ("larder", "sink base", "gola wall"). */
function hitSearchText(h: SearchHit): string {
  const m = h.meta ?? {};
  return [
    h.code, h.desc,
    m["doorRange"], m["doorColour"], m["category"], m["brand"], m["sjbCategories"],
  ].filter((v) => typeof v === "string" && v).join(" ");
}

/**
 * Rank a tab's results against what was typed.
 *
 * Returns `hits` untouched for any tab not opted in, and never narrows — the
 * limit is the incoming length, because this pass exists to reorder. A tab
 * that opts in but returns one row is left alone too.
 */
function rankHits(supplier: string, query: string, hits: SearchHit[]): SearchHit[] {
  const q = query.trim();
  if (!q || !RERANK_TABS.has(supplier) || hits.length < 2) return hits;
  return fuzzyFilter(q, hits, hitSearchText, {
    threshold: RERANK_THRESHOLD,
    limit: hits.length,
  }).map((f) => f.item);
}

import { setActivePicker, setPickerSeed, notifyPickerRemount, setSelectedCab, setSelectedSjbCode, setDoorSupplier, setSjbPanelOpen, getConfiguratorInProgress } from "./picker-selection";
import {
  mountJigsawCabinetPicker,
  mountJigsawAccessoryPicker,
  type JigsawPickerHandle,
  type CollectionConfig,
} from "./jigsaw-cabinet-picker";
import { mountSessionDefaults, type SessionDefaultsHandle } from "./session-defaults";
import type { DtnCabRow, TkcRange, W8Cab, W8Chip, W8VirtualCab } from "./types";
import { DRAWER_PACK_CONFIGS, W8_CABINET_CHIPS } from "./types";

type TkcChip = "All" | TkcDisplayCategory;

/** TKC > Jigsaw collection sub-categories. Cabinets opens the cascade
 *  picker; the rest go straight to a product list (mountJigsawAccessoryPicker).
 *  Steve 2026-05-28 — "Jigsaw chips in the TKC tab".
 *  Aluflute added 2026-05-31 for Rigid Cabs (its sole non-Accessories group). */
type JigsawSubCat = "Cabinets" | "Handles" | "Sinks" | "Taps" | "Lighting" | "Accessories" | "Aluflute";

/** TKC normalised-catalogue collections surfaced as chips in the TKC tab.
 *  Each mounts the shared Jigsaw picker with its own brand config + sub-cats.
 *  Steve 2026-05-28: Jigsaw (4 carcases) + Modus (white-only, fewer chips).
 *  Steve 2026-05-31: Rigid Cabs (4 carcases, same flow as Jigsaw). */
const COLLECTION_KEYS = ["Jigsaw", "Modus", "Rigid"] as const;
type CollectionKey = (typeof COLLECTION_KEYS)[number];
interface CollectionDef {
  label: string;
  cfg: CollectionConfig;
  subCats: readonly JigsawSubCat[];
}
const COLLECTIONS: Record<CollectionKey, CollectionDef> = {
  Jigsaw: {
    label: "Jigsaw",
    cfg: { brand: "Jigsaw", supplierSlug: "tkc", showCarcase: true },
    subCats: ["Cabinets", "Handles", "Sinks", "Taps", "Lighting", "Accessories"],
  },
  Modus: {
    label: "Modus",
    cfg: { brand: "Modus", supplierSlug: "tkc", showCarcase: false },
    subCats: ["Cabinets", "Handles", "Accessories"],
  },
  Rigid: {
    label: "Rigid Cabs",
    cfg: { brand: "Rigid", supplierSlug: "tkc", showCarcase: true },
    // 31 door ranges / 195 colours / 4 carcases / 828 cabinet types. Only
    // accessory groups present are Aluflute (12) + Accessories (1); the
    // Handles/Sinks/Taps/Lighting chips Jigsaw shows would be empty, so omitted.
    subCats: ["Cabinets", "Aluflute", "Accessories"],
  },
};

/** Legacy TKC-catalogue display-categories that are flat product lists, not
 *  cascade pickers. Selecting one shows its products in the LEFT panel (the
 *  search-results list) only — it must NOT open a right-hand picker, which
 *  would render empty (these ItemCategories have no cascade UI). Mirrors how
 *  Jigsaw/Modus accessory chips behave. Keyed on the display name (not the
 *  ItemCategory) because e.g. 'Storage Solutions' also maps to 'storage' but
 *  keeps its cascade picker. (Steve 2026-05-31.) */
const TKC_FLATLIST_CATEGORIES = new Set<TkcDisplayCategory>([
  "Boxed Drawers",
  "Drawer Components",
  "Handles",
  "Vero Accessories",
  "Sinks & Taps",
  "Accessories",
]);

interface Selection {
  supplier: string;
  category: ItemCategory;
  /** TKC tab's display-category chip — only consulted when supplier === 'TKC'. */
  tkcCat: TkcChip;
  /** Active SJB browsing pill; "All" = unfiltered. */
  sjbCat: string;
  /** Active TKC collection (Jigsaw/Modus), or null for the legacy catalogue.
   *  Only consulted when supplier === 'TKC'. */
  collection: CollectionKey | null;
  /** Active collection sub-category chip when `collection` is set. */
  collectionCat: JigsawSubCat;
  /** DTN tab's category chip — populated from /api/dtn/categories. */
  dtnCat: string;
  /** LDL tab's brand chip — populated from /api/ldl/brands. */
  ldlBrand: string;
  /** Generic-catalogue tab's range pill (range_family) — only consulted for a
   *  catalogue-backed supplier that isn't TKC (e.g. PWS). "All" = search-only. */
  catRange: string;
  query: string;
}

// ── Per-supplier chip-strip caches (verbatim source 1925-1948 + 2086-2096) ─
const DTN_CATS_CACHE: { value: string[] | null; loading: boolean; cbs: Array<() => void> } =
  { value: null, loading: false, cbs: [] };
const LDL_BRANDS_CACHE: { value: string[] | null; loading: boolean; cbs: Array<() => void> } =
  { value: null, loading: false, cbs: [] };

async function loadDtnCats(onDone: () => void): Promise<void> {
  if (DTN_CATS_CACHE.value) { onDone(); return; }
  DTN_CATS_CACHE.cbs.push(onDone);
  if (DTN_CATS_CACHE.loading) return;
  DTN_CATS_CACHE.loading = true;
  try {
    const data = await api<{ categories?: Record<string, string[]> }>("/dtn/categories");
    // /api/dtn/categories returns `{ categories: { Cabinet: [...], Door: [...] } }`.
    DTN_CATS_CACHE.value = Object.keys(data.categories ?? {}).sort();
  } catch { DTN_CATS_CACHE.value = []; }
  DTN_CATS_CACHE.loading = false;
  for (const cb of DTN_CATS_CACHE.cbs) cb();
  DTN_CATS_CACHE.cbs = [];
}

async function loadLdlBrands(onDone: () => void): Promise<void> {
  if (LDL_BRANDS_CACHE.value) { onDone(); return; }
  LDL_BRANDS_CACHE.cbs.push(onDone);
  if (LDL_BRANDS_CACHE.loading) return;
  LDL_BRANDS_CACHE.loading = true;
  try {
    const data = await api<{ brands?: Array<{ brand: string }> }>("/ldl/brands");
    LDL_BRANDS_CACHE.value = (data.brands ?? []).map((b) => b.brand).filter(Boolean);
  } catch { LDL_BRANDS_CACHE.value = []; }
  LDL_BRANDS_CACHE.loading = false;
  for (const cb of LDL_BRANDS_CACHE.cbs) cb();
  LDL_BRANDS_CACHE.cbs = [];
}

// TKC door-ranges cache (source CAT.tkcRanges) — used by the TKC+Doors+
// empty-query view to render the 39 door range cards verbatim 1816-1847.
/* ── TKC door-range cards ──────────────────────────────────────────────
 *
 * Hoisted out of paintResults 2026-09-05 so the same cards can be rendered
 * from TWO places: the empty-box browse view, and above the product results
 * when something is typed.
 *
 * Until then the card view was gated on `!sel.query.trim()`, so typing
 * REPLACED the ranges with individual door products. Typing "cartmel" never
 * narrowed the 47 cards to the one you wanted — it swapped them for 1,122
 * product rows capped at 50. The cards were the only way to reach the door
 * configurator, and the search box was the only way to type, and the two
 * were mutually exclusive. */
type CardTier = "stock" | "pto" | "paintplus" | "signature" | "bespoke" | "primed";

const TIER_LABEL: Record<CardTier, string> = {
  stock: "", pto: "Paint to Order", paintplus: "Paint Plus", signature: "Signature",
  bespoke: "Bespoke", primed: "Primed",
};
const TIER_HINT: Record<CardTier, string> = {
  stock:     "Stocked colours",
  pto:       "Paint to order · longer lead time",
  paintplus: "Paint Plus · stock price, longer lead time",
  signature: "Signature · stock lead time",
  bespoke:   "Bespoke · custom colour reference",
  primed:    "Primed · ready to paint on site",
};
// Paint Plus sits next to stock: it is stocked PRICE, so it is the nearer
// upsell from a stocked colour than full paint-to-order is.
const TIER_ORDER: readonly CardTier[] = ["stock", "paintplus", "pto", "signature", "bespoke", "primed"];

interface RangeCard { range: TkcRange; tier: CardTier; label: string; colourCount: number; }

/** Expand each range into up to 5 cards, one per stockType that has actual
 *  product rows. Stock always renders if there are stock products;
 *  PTO/Signature/Bespoke/Primed render only when products exist for that tier
 *  (Steve, 2026-05-26 — "Only show PTO and Bespoke cards when products exist
 *  for those stock types"). */
function buildRangeCards(ranges: readonly TkcRange[]): RangeCard[] {
  const cards: RangeCard[] = [];
  for (const r of ranges) {
    const has = r.hasProductsByType;
    const cbt = r.coloursByType;
    for (const tier of TIER_ORDER) {
      // Without hasProductsByType (legacy server) fall back to "show every
      // tier that has colours" — preserves old behaviour.
      //
      // Both buckets are indexed defensively. The types say every tier key is
      // present, but these objects come off the wire: a server older than the
      // tier (pre-2026-05-26 for pto/signature, pre-2026-09-09 for paintplus)
      // simply omits the key, and `cbt?.[tier].length` guarded only `cbt`
      // itself — so a missing key threw and took the whole products panel down
      // rather than hiding one card.
      const showTier = has ? (has[tier] ?? false) : (cbt?.[tier]?.length ?? 0) > 0;
      if (!showTier) continue;
      cards.push({
        range: r, tier,
        label: tier === "stock" ? r.name : `${r.name} ${TIER_LABEL[tier]}`,
        colourCount: cbt ? (cbt[tier]?.length ?? 0) : r.colours.length,
      });
    }
  }
  return cards;
}

/**
 * Rank range cards against a typed query.
 *
 * Uses the platform's own fuzzy engine (fuzzy.ts — the one the pickers' combo
 * dropdowns already use), NOT the server's matcher. The server tokenises and
 * ANDs `LIKE %tok%`, which is good at narrowing 26,285 products and useless at
 * a typo: "cartmell" returns zero rows however many columns it looks in.
 * Against 47 cards, scoring every one costs nothing and buys single-edit
 * tolerance — "cartmell" -> Cartmel, "cambrige" -> Cambridge, "windsr" ->
 * Windsor.
 *
 * `lev1` is a true Levenshtein-1, so it does NOT catch transpositions:
 * "carmtel" and "oxfrod" still score zero. Deliberately left alone — adding a
 * Damerau step means changing a module every combo dropdown on the platform
 * depends on, which is its own piece of work.
 *
 * Threshold 120 rather than fuzzyFilter's default 30: a subsequence match
 * scores from 60 and would let "sn" pull in half the list. 130 is the lev1
 * band, so 120 admits a one-character typo and rejects loose letter soup.
 */
const RANGE_CARD_MATCH_THRESHOLD = 120;

function matchRangeCards(query: string, cards: RangeCard[]): RangeCard[] {
  const q = query.trim();
  if (!q) return [];
  return fuzzyFilter(q, cards, (c) => c.label, {
    threshold: RANGE_CARD_MATCH_THRESHOLD, limit: 8,
  }).map((h) => h.item);
}

/** The card list markup. `data-card-idx` indexes into whatever array was
 *  passed, so the browse view and the search view wire identically. */
function renderRangeCardList(cards: readonly RangeCard[]): string {
  return `
    <ul class="qb-products__results-list qb-products__range-cards">
      ${cards.map((c, i) => `
        <li class="qb-product-hit qb-range-card" data-card-idx="${i}" tabindex="0" role="button" aria-label="Configure ${escapeHtml(c.label)}">
          <div class="qb-product-hit__main">
            <div class="qb-product-hit__desc">${escapeHtml(c.label)}</div>
            <div class="qb-product-hit__meta">
              <span class="qb-product-hit__code">${escapeHtml(TIER_HINT[c.tier])}</span>
              ${c.colourCount ? `<span class="qb-product-hit__dim">${c.colourCount} colour${c.colourCount === 1 ? "" : "s"}</span>` : ""}
            </div>
          </div>
          <div class="qb-product-hit__price">Pick</div>
        </li>
      `).join("")}
    </ul>`;
}

/**
 * Two SJB pills that are NOT recipe categories.
 *
 * `Panel by size` is not a browse at all -- it opens the panel picker,
 * because 1482 panel rows are not a list anyone can shop from and the sizes
 * a retailer wants are mostly not stocked exactly. `Cut & Drill` IS a browse,
 * over DTN's processing charges, which are ordinary direct-add lines.
 *
 * Kept as named constants because three separate places have to agree on the
 * strings: the chip row, the endpoint resolver, and the click handler.
 */
const SJB_PILL_PANEL = "Panel by size";
const SJB_PILL_PROCESSING = "Cut & Drill";

/* SJB browsing categories. Server-derived from the recipe (2026-09-05), so a
 * category that leaves the file stops being offered without a client change.
 * Same cache-and-repaint shape as DTN_CATS_CACHE. */
const SJB_CATS_CACHE: { value: SjbCategory[] | null; loading: boolean } = { value: null, loading: false };

async function loadSjbCats(onDone: () => void): Promise<void> {
  if (SJB_CATS_CACHE.loading) return;
  SJB_CATS_CACHE.loading = true;
  try { SJB_CATS_CACHE.value = await loadSjbCategories(); }
  catch { SJB_CATS_CACHE.value = []; }
  finally { SJB_CATS_CACHE.loading = false; onDone(); }
}

const TKC_RANGES_CACHE: { value: TkcRange[] | null; loading: boolean; cbs: Array<() => void> } =
  { value: null, loading: false, cbs: [] };

async function loadTkcRanges(onDone: () => void): Promise<void> {
  if (TKC_RANGES_CACHE.value) { onDone(); return; }
  TKC_RANGES_CACHE.cbs.push(onDone);
  if (TKC_RANGES_CACHE.loading) return;
  TKC_RANGES_CACHE.loading = true;
  try {
    // Repointed 2026-05-24 from /tkc/door-ranges → PG-backed
    // /catalogue/door-ranges (GROUP BY specification->>'doorRange').
    const data = await api<{ ranges?: TkcRange[] }>("/catalogue/door-ranges?supplier=tkc");
    TKC_RANGES_CACHE.value = Array.isArray(data.ranges) ? data.ranges : [];
  } catch { TKC_RANGES_CACHE.value = []; }
  TKC_RANGES_CACHE.loading = false;
  for (const cb of TKC_RANGES_CACHE.cbs) cb();
  TKC_RANGES_CACHE.cbs = [];
}

// Per-supplier door-ranges cache for the generic-catalogue hybrid view (PWS
// and any future imported door catalogue). Keyed by slug — mirrors
// TKC_RANGES_CACHE but parameterised so each supplier caches independently.
type CatRangesEntry = { value: TkcRange[] | null; loading: boolean; cbs: Array<() => void> };
const CATALOGUE_RANGES_CACHE = new Map<string, CatRangesEntry>();

function catRangesEntry(slug: string): CatRangesEntry {
  let entry = CATALOGUE_RANGES_CACHE.get(slug);
  if (!entry) { entry = { value: null, loading: false, cbs: [] }; CATALOGUE_RANGES_CACHE.set(slug, entry); }
  return entry;
}

async function loadCatalogueRanges(slug: string, onDone: () => void): Promise<void> {
  const entry = catRangesEntry(slug);
  if (entry.value) { onDone(); return; }
  entry.cbs.push(onDone);
  if (entry.loading) return;
  entry.loading = true;
  try {
    const data = await api<{ ranges?: TkcRange[] }>(
      `/catalogue/door-ranges?supplier=${encodeURIComponent(slug)}`,
    );
    entry.value = Array.isArray(data.ranges) ? data.ranges : [];
  } catch { entry.value = []; }
  entry.loading = false;
  for (const cb of entry.cbs) cb();
  entry.cbs = [];
}

// TKC catalogue sections cache — the high-level pill nav (Doors, Cabinets, …)
// is driven by which sections the uploaded CSVs actually populate, served by
// GET /catalogue/tkc-sections. No more hardcoded category strip (Steve
// 2026-05-31). Sections arrive in canonical display order.
interface TkcSection { section: string; categories: string[]; }
const TKC_SECTIONS_CACHE: { value: TkcSection[] | null; loading: boolean; cbs: Array<() => void> } =
  { value: null, loading: false, cbs: [] };

async function loadTkcSections(onDone: () => void): Promise<void> {
  if (TKC_SECTIONS_CACHE.value) { onDone(); return; }
  TKC_SECTIONS_CACHE.cbs.push(onDone);
  if (TKC_SECTIONS_CACHE.loading) return;
  TKC_SECTIONS_CACHE.loading = true;
  try {
    const data = await api<{ sections?: Array<{ section: string; categories?: string[] }> }>(
      "/catalogue/tkc-sections?supplier=tkc",
    );
    TKC_SECTIONS_CACHE.value = Array.isArray(data.sections)
      ? data.sections
          .filter((s) => s && s.section)
          .map((s) => ({ section: s.section, categories: Array.isArray(s.categories) ? s.categories : [] }))
      : [];
  } catch { TKC_SECTIONS_CACHE.value = []; }
  TKC_SECTIONS_CACHE.loading = false;
  for (const cb of TKC_SECTIONS_CACHE.cbs) cb();
  TKC_SECTIONS_CACHE.cbs = [];
}

// Generic-catalogue range pills — a non-TKC catalogue-backed supplier (e.g.
// PWS) drives its pill nav from the distinct range_family values its catalogue
// contains, served by GET /catalogue/sections?id=<slug>. Keyed per supplier
// slug (mirrors the per-supplier DTN/LDL caches but parameterised). A supplier
// whose catalogue has no range_family resolves to [] → the client falls back
// to search-only instead of the misleading generic ITEM_CATEGORIES pills.
interface CatSection { name: string; productCount: number; }
type CatSectionsEntry = { value: CatSection[] | null; loading: boolean; cbs: Array<() => void> };
const CATALOGUE_SECTIONS_CACHE = new Map<string, CatSectionsEntry>();

function catSectionsEntry(slug: string): CatSectionsEntry {
  let entry = CATALOGUE_SECTIONS_CACHE.get(slug);
  if (!entry) { entry = { value: null, loading: false, cbs: [] }; CATALOGUE_SECTIONS_CACHE.set(slug, entry); }
  return entry;
}

async function loadCatalogueSections(slug: string, onDone: () => void): Promise<void> {
  const entry = catSectionsEntry(slug);
  if (entry.value) { onDone(); return; }
  entry.cbs.push(onDone);
  if (entry.loading) return;
  entry.loading = true;
  try {
    const data = await api<{ sections?: Array<{ name: string; productCount?: number }> }>(
      `/catalogue/sections?id=${encodeURIComponent(slug)}`,
    );
    entry.value = Array.isArray(data.sections)
      ? data.sections
          .filter((s) => s && s.name)
          .map((s) => ({ name: s.name, productCount: Number(s.productCount) || 0 }))
      : [];
  } catch { entry.value = []; }
  entry.loading = false;
  for (const cb of entry.cbs) cb();
  entry.cbs = [];
}

/** GROUP_NAME categories backing a TKC off-the-shelf section, used to fetch
 *  the whole section by category (reliable for component rows whose name has
 *  no section keyword). Empty for "All", "Doors" (door picker) and unknowns. */
function tkcSectionCategories(section: string): string[] {
  if (!section || section === "All" || section === "Doors") return [];
  const entry = (TKC_SECTIONS_CACHE.value ?? []).find((s) => s.section === section);
  return entry?.categories ?? [];
}

// ── Per-section client-side filter dropdowns ───────────────────────────────
// Some TKC sections offer dropdowns above the product list that narrow the
// (already category-fetched) results client-side. Each option's `test` gets
// the product description (UPPER-cased) + its GROUP_NAME/category (lower-cased);
// colour/finish sections key off the category, Handles keys off the
// description. Multiple dropdowns in a section AND together. "All" = no filter.
interface SecFilterOption { label: string; test: (descUpper: string, catLower: string) => boolean; }
interface SecFilter { key: string; label: string; options: ReadonlyArray<SecFilterOption>; }

const SECTION_FILTERS: Record<string, ReadonlyArray<SecFilter>> = {
  "Quick Cabs": [
    { key: "qc-colour", label: "Cabinet Colour", options: [
      { label: "White",     test: (_d, c) => c.includes("white carcases") },
      { label: "Oak",       test: (_d, c) => c.includes("oak carcases") },
      { label: "Dust Grey", test: (_d, c) => c.includes("dust grey carcases") },
    ] },
  ],
  "Handles": [
    { key: "h-finish", label: "Finish", options: [
      { label: "Chrome",         test: (d) => /CHROME/.test(d) },
      { label: "Brushed Steel",  test: (d) => /STEEL/.test(d) },
      { label: "Matt Black",     test: (d) => /BLACK/.test(d) },
      { label: "Antique Copper", test: (d) => /COPPER/.test(d) },
      { label: "Satin/Nickel",   test: (d) => /SATIN|NICKEL/.test(d) },
      { label: "Brass",          test: (d) => /BRASS/.test(d) },
      { label: "Pewter",         test: (d) => /PEWTER/.test(d) },
    ] },
    { key: "h-type", label: "Type", options: [
      { label: "Bar Handle", test: (d) => /BAR HANDL/.test(d) },
      { label: "Cup Handle", test: (d) => /\bCUP\b/.test(d) },
      { label: "Knob",       test: (d) => /\bKNOB\b/.test(d) },
      { label: "Bow Handle", test: (d) => /\bBOW\b/.test(d) },
      { label: "T-Bar",      test: (d) => /\bT[ -]?BAR\b/.test(d) },
    ] },
  ],
  "Vero Accessories": [
    { key: "v-finish", label: "Finish", options: [
      { label: "Silver",        test: (_d, c) => c.includes("silver") },
      { label: "Black",         test: (_d, c) => c.includes("black") },
      { label: "Graphite",      test: (_d, c) => c.includes("graphite") },
      { label: "Brushed Brass", test: (_d, c) => c.includes("brushed brass") },
      { label: "Rose Gold",     test: (_d, c) => c.includes("rose gold") },
    ] },
  ],
  // Seven accessory GROUP_NAMEs under one pill; filter by category (the dropdown
  // labels differ slightly from the GROUP_NAMEs — "Wirework"↔"Dyn Wireworks",
  // "Edging Tape"↔"Edging" — so each option keys off a distinctive substring).
  "Accessories": [
    { key: "acc-cat", label: "Category", options: [
      { label: "Cutlery Trays",     test: (_d, c) => c.includes("cutlery") },
      { label: "Wirework",          test: (_d, c) => c.includes("wirework") },
      { label: "Edging Tape",       test: (_d, c) => c.includes("edging") },
      { label: "Hinges & Plates",   test: (_d, c) => c.includes("hinge") },
      { label: "Legs & Supports",   test: (_d, c) => c.includes("legs") || c.includes("support") },
      { label: "Lighting",          test: (_d, c) => c.includes("lighting") },
      { label: "Wardrobe Fittings", test: (_d, c) => c.includes("wardrobe") },
    ] },
  ],
};

interface SearchHit {
  code: string;
  desc: string;
  listPrice: number;
  /** Width / height / depth in mm. 0 if not provided by the source. */
  w: number;
  h: number;
  d: number;
  /** Supplier-specific extras stashed on the line's metadata. */
  meta?: Record<string, unknown>;
  /**
   * Which supplier this ROW actually came from.
   *
   * Absent on the single-supplier tabs, where the tab IS the answer. Present
   * on anything served by /catalogue/search, which has returned
   * supplierSlug + supplierName per row since 2026-08-19 — the right-hand
   * add-item box already reads them; the left panel never did.
   *
   * That gap is not theoretical. "My Catalogue" asks /catalogue/search with
   * NO supplier id, which is the server's "every catalogue this tenant is
   * granted" path, so it returns TKC and PWS rows — and every one of them was
   * committed as supplier "Custom" with Custom's (non-existent) discounts,
   * because the supplier was read from the tab. See hitSupplier().
   */
  supplierSlug?: string;
  supplierName?: string;
  /** Which TAB's endpoint produced this row. Set only by the "All catalogues"
   *  fan-out, where the active tab is no longer the answer to "what is this
   *  and how do I configure it". Routing reads it; everything else falls back
   *  to the active tab, so a single-supplier tab is unchanged. */
  sourceTab?: string;
}

const DEFAULT_SELECTION: Selection = {
  supplier: "TKC",
  // `sundries`, not `cabinets` (2026-08-28). This field is only reachable via
  // the generic ITEM_CATEGORIES chip strip, which no live tab renders — every
  // real tab has its own strip (TKC sections, DTN categories, LDL brands, PWS
  // ranges). It was therefore stuck on "cabinets" forever on the DTN and LDL
  // tabs, and it drove picker routing: every search hit opened the cabinet
  // picker, which then refused to mount without a selected cab and showed an
  // empty pane. Routing now comes from the hit's own catalogue fields; this
  // value survives only as the fallback for a manual "+ Add custom" line,
  // where sundries is both correct and harmless.
  category: "sundries",
  tkcCat: "All",
  sjbCat: "All",
  collection: null,
  collectionCat: "Cabinets",
  dtnCat: "All",
  ldlBrand: "All",
  catRange: "All",
  query: "",
};

// ── Dynamic, access-gated supplier tabs ──────────────────────────────────────
// catalogue_products-backed suppliers (TKC, PWS, any future import) are DYNAMIC:
// they come from /api/catalogue/suppliers, which returns only the catalogues the
// signed-in tenant has been GRANTED (admin-controlled). The non-catalogue tabs
// (DTN/LDL live APIs, CUSTOM = the retailer's own) are always present. Until the
// grant list resolves we show only the always-on tabs.
/**
 * "All catalogues" — one search across every catalogue this retailer can see.
 *
 * A PSEUDO-TAB. It has no endpoint of its own because there is no such
 * endpoint: the five tabs are served by four different backends (Postgres for
 * TKC/PWS and any import, a CSV in memory for DTN, a 65k-row JSON for LDL,
 * recipe files priced live for SJB) with four different wire shapes. Searching
 * everything is a fan-out and a merge, not a widened query.
 *
 * SCOPE IS THE TAB LIST, deliberately. It asks exactly the catalogues the
 * retailer already has pills for — which are already access-gated, per tenant,
 * by the server (/api/catalogue/suppliers for the PG ones, a probe for SJB).
 * So this widens what is CONVENIENT, never what is permitted: there is no
 * request here the retailer could not already make by clicking a pill.
 */
const ALL_TAB_KEY = "ALL";
const ALL_TAB: SupplierDef = {
  key: ALL_TAB_KEY,
  label: "All catalogues",
  // Never read. Every row in this list names its own supplier — that is the
  // precondition for the tab existing at all (see hitSupplier).
  supplierKey: "",
  model: "completes",
  colour: "#94a3b8",
  defaultDiscPct: 0,
  mode: "live",
};

/** Rows taken from any ONE catalogue. A cap per supplier, not just an overall
 *  one: without it the largest catalogue fills the window and the smaller ones
 *  are invisible, which is the failure the whole feature exists to fix. */
const PER_SUPPLIER_LIMIT = 12;
/** Rows rendered in total, for every tab. Was inline in renderHitList. */
const FLAT_RESULT_CAP = 50;

const isAllTab = (key: string): boolean => key === ALL_TAB_KEY;

const ALWAYS_TABS: readonly SupplierDef[] = SUPPLIERS.filter((s) => !s.catalogueSlug);
const CATALOGUE_DEFS: readonly SupplierDef[] = SUPPLIERS.filter((s) => !!s.catalogueSlug);
let supplierTabs: SupplierDef[] = [ALL_TAB, ...ALWAYS_TABS];

const CATALOGUE_PALETTE = ["#6366f1", "#0891b2", "#7c3aed", "#c2410c", "#0d9488", "#be123c"];


/** A flat-search tab for a catalogue supplier that has no bespoke config (PWS, …). */
function genericCatalogueDef(slug: string, name: string, i: number): SupplierDef {
  return {
    key: slug.toUpperCase(),
    label: name,
    supplierKey: name,
    model: "completes",
    colour: CATALOGUE_PALETTE[i % CATALOGUE_PALETTE.length]!,
    defaultDiscPct: 0,
    mode: "live",
    api: "/catalogue/search",
    catalogueSlug: slug,
  };
}

/** SJB's cabinet catalogue is tenant-gated server-side (/api/sjb-catalogue is
 *  403 for everyone else). Probe it rather than hard-coding the tenant here:
 *  the server stays the single source of truth for who may see it, and adding
 *  a second tenant needs no client change. */
let sjbGranted = false;

async function probeSjbAccess(): Promise<void> {
  try {
    await api<{ ok: boolean }>("/sjb-catalogue/health");
    sjbGranted = true;
  } catch {
    sjbGranted = false;
  }
}

/** Fetch the tenant's GRANTED catalogue suppliers and rebuild the tab list. */
async function loadCatalogueTabs(): Promise<void> {
  // Filter SJB out rather than appending it when granted, so the tab keeps its
  // declared position in SUPPLIERS (…DTN, LDL, SJB, My Catalogue) instead of
  // landing after "My Catalogue".
  const alwaysTabs = (): SupplierDef[] =>
    sjbGranted ? [...ALWAYS_TABS] : ALWAYS_TABS.filter((x) => x.key !== "SJB");
  await probeSjbAccess();
  try {
    const data = await api<{ suppliers?: Array<{ slug: string; name: string }> }>("/catalogue/suppliers");
    const granted = Array.isArray(data.suppliers) ? data.suppliers : [];
    const defs = granted.map((s, i) =>
      CATALOGUE_DEFS.find((c) => c.catalogueSlug === s.slug) ?? genericCatalogueDef(s.slug, s.name, i),
    );
    supplierTabs = [ALL_TAB, ...defs, ...alwaysTabs()];
  } catch {
    supplierTabs = [ALL_TAB, ...alwaysTabs()];
  }
}

/**
 * Which supplier a search hit belongs to, and what the quote line must call it.
 *
 * THE TAB IS NOT THE ANSWER. Four places used to read the supplier off the
 * active tab — the line, the discount lookup, the picker seed and direct-add
 * — which is correct only while every row in the list comes from one
 * catalogue. "My Catalogue" already breaks that: it asks /catalogue/search
 * with no supplier id, gets TKC and PWS rows back, and labelled every one of
 * them "Custom".
 *
 * TWO DIFFERENT KEYS, deliberately:
 *
 *   `slug`  keys the DISCOUNT cache (discounts.ts lowercases it). For a
 *           catalogue row it is the row's own slug; otherwise the tab's, which
 *           for TKC/PWS is the slug uppercased and for DTN/LDL is the tab id
 *           — exactly what was passed before, so nothing moves.
 *
 *   `label` is the string STORED ON THE LINE, and it is resolved through the
 *           TAB REGISTRY rather than taken from the catalogue`s own `name`
 *           column. That column says "TKC Components" where every tab and
 *           every existing quote line says "TKC". Purchase orders match
 *           suppliers on the name string, so writing the other spelling would
 *           split one supplier into two — there is already one live line
 *           reading "TKC Components" against 267 reading "TKC", written by the
 *           right-hand add-item box, which does take the raw column.
 *
 * Falls back to the tab at every step, so a single-supplier tab behaves
 * exactly as it did.
 */
function hitSupplier(hit: SearchHit | undefined, tabKey: string): { slug: string; label: string } {
  // No `sourceTab` fallback here on purpose. Every row the fan-out returns is
  // stamped with its own slug and name (stampSource), so this branch would be
  // unreachable — and an unreachable fallback reads as a safety net that is
  // not there. `tabKey` is the fallback for a row from an ordinary tab, which
  // carries no supplier of its own.
  const tabDef = supplierTabs.find((s) => s.key === tabKey);
  const rowSlug = hit?.supplierSlug ?? "";
  return {
    slug: rowSlug || tabDef?.catalogueSlug || tabKey,
    // supplierLabelForSlug is shared with the right-hand add-item box, so the
    // two surfaces cannot spell the same supplier two ways — which is exactly
    // what they were doing.
    label: (rowSlug ? supplierLabelForSlug(rowSlug, hit?.supplierName) : "")
        || tabDef?.supplierKey || tabKey,
  };
}

/** Per-(tab, category) endpoint resolver. Returns null if no live search
 *  applies — UI falls back to "+ Add custom".
 *
 *  Verbatim source PROD_TABS (kos-qb-v3.js:1666–1671):
 *    TKC    → /tkc/search
 *    DTN    → /dtn/search
 *    LDL    → /ldl/search
 *    CUSTOM → /catalogue/search   (with ?type=supplier)
 *
 *  TKC owns its own chip strip + filtering (Item 3 — `tkcInferCategory`
 *  + TKC_CATEGORY_ORDER); the manual door picker form coexists with the
 *  search results when the 'Doors' chip is active.
 */
/** Set by mountProductsPanel so the module-scope endpoint resolver can read
 *  the quote-level SJB defaults without the store being threaded through it. */
let sjbDefaultsRef: () => { boardKey: string; hingeFinish: string; drawerSystem: string } =
  () => ({ boardKey: "", hingeFinish: "standard", drawerSystem: "antaro" });

function resolveEndpoint(supplier: string, _category: ItemCategory, query: string, sel?: Selection, categories?: string[]): string | null {
  // "All catalogues" resolves to no single url by construction — runAllSearch
  // owns it. Callers that ask "is there a live search here?" must test
  // isAllTab() as well, or the panel renders its no-endpoint empty state over
  // a tab that searches more than any other.
  if (isAllTab(supplier)) return null;
  const def = supplierTabs.find((s) => s.key === supplier);
  if (!def || !def.api) return null;
  const trimmed = query.trim();
  const q = encodeURIComponent(trimmed);
  // Catalogue-backed supplier (TKC, PWS, …) section fetch by GROUP_NAME category
  // — works with an empty query so a section pill shows its full catalogue (incl.
  // component rows whose name has no section keyword). A typed query narrows it.
  if (def.catalogueSlug && categories && categories.length > 0) {
    const cats = categories.map((c) => `&category=${encodeURIComponent(c)}`).join("");
    return `${def.api}?type=supplier&id=${encodeURIComponent(def.catalogueSlug)}&limit=500${trimmed ? `&q=${q}` : ""}${cats}`;
  }
  // SJB is checked BEFORE the empty-query bail: a category pill is a BROWSE
  // affordance, so "Corner Base" with nothing typed has to fetch that
  // category. Every other tab still needs a query to search.
  if (def.key === "SJB") {
    const sjbCat = sel && sel.sjbCat && sel.sjbCat !== "All" ? sel.sjbCat : "";
    // Cut & Drill is a different endpoint, not a category filter on the
    // cabinet search. Checked before the empty-query bail for the same
    // reason a category pill is: clicking it with an empty box must list.
    if (sjbCat === SJB_PILL_PROCESSING) {
      return `/sjb-catalogue/processing${trimmed ? `?q=${q}` : ""}`;
    }
    // The panel pill opens a picker and never searches.
    if (sjbCat === SJB_PILL_PANEL) return null;
    if (!trimmed && !sjbCat) return null;
    const d = sjbDefaultsRef();
    const colourQs = d.boardKey
      ? `&colour=${encodeURIComponent(d.boardKey)}&hinge=${d.hingeFinish}&drawer=${d.drawerSystem}`
      : "";
    // A pill is a BROWSING shortcut, not a filter that persists into search
    // (Steve, 2026-09-05). With an empty box it narrows the list; the moment
    // a term is typed it is ignored and the whole catalogue is searched.
    //
    // The old behaviour scoped the search to the pill, which quietly made the
    // catalogue look smaller than it is: sitting on "Larder" and typing
    // "belfast" returned nothing, and nothing on screen explained why. A
    // retailer who types a term is asking the catalogue a question, not
    // refining a browse.
    const catQs = (!trimmed && sjbCat) ? `&category=${encodeURIComponent(sjbCat)}` : "";
    return `${def.api}?q=${q}&limit=200${colourQs}${catQs}`;
  }
  if (!trimmed) return null;
  if (def.key === "CUSTOM") {
    return `${def.api}?q=${q}&type=supplier`;
  }
  // Any catalogue_products supplier (TKC since 2026-05-24, PWS, future imports)
  // resolves `id=<slug>` → suppliers.id server-side and filters products to it.
  if (def.catalogueSlug) {
    // Narrow to the active range pill on a generic catalogue (PWS, …). The
    // server has supported `&range=<range_family>` since 2026-08-26; the
    // client simply never sent it, because the generic tab had no flat search
    // to send it from. With it, "Porter" + "shaker" searches inside Porter's
    // 1,598 rows instead of the whole 13,664-row catalogue.
    const rangeQs = sel && sel.catRange && sel.catRange !== "All"
      ? `&range=${encodeURIComponent(sel.catRange)}`
      : "";
    // Spread the row budget across range+colour groups instead of spending it
    // all inside the first one. Without this, ordering is by product_code and a
    // range's colours sit contiguously, so the 50-row window closes inside the
    // first colour: "porter" returned 50 rows of Porter Gloss Cashmere and
    // Porter Matt White was unreachable however you typed. The server has
    // supported this since 2026-08-26 (it is what fixed the same bug in the
    // right-column Add-item box) and falls back to plain product_code order by
    // itself once the query resolves to a single range+colour, so naming a
    // colour still spends the whole budget on that door's SIZES.
    return `${def.api}?q=${q}&type=supplier&id=${encodeURIComponent(def.catalogueSlug)}&limit=50&groupBy=range_colour${rangeQs}`;
  }
  // DTN supports `?category=` filter server-side (dtn_search.js:313).
  if (def.key === "DTN" && sel && sel.dtnCat && sel.dtnCat !== "All") {
    return `${def.api}?q=${q}&limit=50&category=${encodeURIComponent(sel.dtnCat)}`;
  }
  return `${def.api}?q=${q}&limit=50`;
}

/** dtn_search.js result shape (modules/dtn_search.js:100–113). */
interface DtnRaw {
  code?: string; desc?: string;
  w?: number; h?: number; d?: number;
  price?: number; flatPrice?: number;
  priceGroups?: number[]; hasPriceGroups?: boolean;
  category?: string; subcategory?: string;
  doorSizes?: string; supplier?: string; notes?: string;
}
interface LdlRaw {
  code?: string; desc?: string;
  price?: number; listPrice?: number; tradePrice?: number; discPct?: number;
  cat?: string; brand?: string; status?: string;
}
/** catalogue.js QB result shape (modules/catalogue.js:192–200). */
interface CustomRaw {
  sku?: string; desc?: string;
  price?: number;
  h?: number; w?: number; d?: number;
  range?: string; colour?: string; category?: string;
  /** Per-row supplier, served since 2026-08-19 (catalogue.js SEARCH_COLS). */
  supplierSlug?: string; supplierName?: string;
  /** The supplier's discount code (PWS "BMV"), served since 2026-10-01. */
  discountCode?: string;
}

function normaliseDtn(r: DtnRaw): SearchHit {
  // dtn_search.js returns `price` already resolved to pg1 (or flat price for
  // non-cabinet rows). DTN list prices include +15% retailer markup
  // server-side, per source SUPPLIER_MODELS note (kos-qb-v3.js:67).
  return {
    code: String(r.code ?? ""),
    desc: String(r.desc ?? ""),
    listPrice: Number(r.price ?? 0),
    w: Number(r.w ?? 0),
    h: Number(r.h ?? 0),
    d: Number(r.d ?? 0),
    meta: {
      ...(r.category !== undefined && { category: r.category }),
      ...(r.subcategory !== undefined && { subcategory: r.subcategory }),
      ...(r.hasPriceGroups !== undefined && { hasPriceGroups: r.hasPriceGroups }),
      ...(Array.isArray(r.priceGroups) && { priceGroups: r.priceGroups }),
    },
  };
}

function normaliseLdl(r: LdlRaw): SearchHit {
  return {
    code: String(r.code ?? ""),
    desc: String(r.desc ?? ""),
    listPrice: Number(r.listPrice ?? r.price ?? 0),
    w: 0, h: 0, d: 0,
    meta: {
      ...(r.cat !== undefined && { category: r.cat }),
      ...(r.brand !== undefined && { brand: r.brand }),
      ...(r.status !== undefined && { status: r.status }),
    },
  };
}

function normaliseCustom(r: CustomRaw): SearchHit {
  return {
    code: String(r.sku ?? ""),
    desc: String(r.desc ?? ""),
    listPrice: Number(r.price ?? 0),
    w: Number(r.w ?? 0),
    h: Number(r.h ?? 0),
    d: Number(r.d ?? 0),
    // Only when the server said so. An empty string would be indistinguishable
    // from "this endpoint does not know", and the fallback in hitSupplier()
    // depends on telling those apart.
    ...(r.supplierSlug ? { supplierSlug: r.supplierSlug } : {}),
    ...(r.supplierName ? { supplierName: r.supplierName } : {}),
    meta: {
      ...(r.range !== undefined && { doorRange: r.range }),
      ...(r.colour !== undefined && { doorColour: r.colour }),
      ...(r.category !== undefined && { category: r.category }),
      ...(r.discountCode ? { discountCode: r.discountCode } : {}),
    },
  };
}

interface SjbRaw {
  code?: string; desc?: string; dims?: string; doorConfig?: string; handing?: string;
  carcassList?: number; discPct?: number; discountLabel?: string;
  hardwareTotal?: number; tradePrice?: number; blocked?: unknown[];
  /** Recipe browsing categories (2026-09-05). Pipe-delimited in the source,
   *  already split server-side. */
  categories?: string[];
  notes?: string;
}

/** SJB rows arrive already priced for the quote's colour (or unpriced when no
 *  colour is set yet). `listPrice` carries the TRADE price here, because that
 *  is the only number that means anything on an SJB row — the carcass list
 *  alone excludes hardware and ignores the board adjustment. It is display
 *  only: the quote line itself is built from a fresh, authoritative
 *  priceSjbLine call so the panel and the line can never disagree. */
function normaliseSjb(r: SjbRaw): SearchHit {
  const dims = String(r.dims ?? "").match(/(\d+)\s*x\s*(\d+)\s*x\s*(\d+)/i);
  return {
    code: String(r.code ?? ""),
    desc: String(r.desc ?? ""),
    listPrice: Number(r.tradePrice ?? 0),
    w: dims ? Number(dims[2]) : 0,
    h: dims ? Number(dims[1]) : 0,
    d: dims ? Number(dims[3]) : 0,
    meta: {
      // The ITEM category — what the line is filed as on the quote. Every SJB
      // row is a cabinet, so this is a constant and always has been.
      category: "Cabinet",
      // The recipe's BROWSING categories ("Larder", "Gola Wall", "Sink
      // Base"), which are a different thing entirely and were not reaching
      // the client at all. The server already matches on them; without this
      // the client re-rank ranked blind to the one field a retailer is most
      // likely to type — "larder" ranked 95 larders by description alone.
      ...(Array.isArray(r.categories) && r.categories.length > 0
        && { sjbCategories: r.categories.join(" ") }),
      ...(r.notes && { sjbNotes: r.notes }),
      ...(r.discountLabel !== undefined && { sjbDiscountLabel: r.discountLabel }),
      ...(r.hardwareTotal !== undefined && { sjbHardwareTotal: r.hardwareTotal }),
      ...(r.doorConfig !== undefined && { doorConfig: r.doorConfig }),
      ...(Array.isArray(r.blocked) && r.blocked.length > 0 && { sjbBlocked: r.blocked.length }),
    },
  };
}

interface SjbProcessingRaw {
  code?: unknown; desc?: unknown; unit?: unknown; price?: unknown; alreadyNet?: unknown;
}

/** A DTN processing charge as a search row. These are ordinary priced lines —
 *  no configuration, no picker — so they direct-add like any other product.
 *  The unit is appended to the description because "Cut only" at GBP 2.97
 *  means nothing without "per panel" beside it, and the quantity box is
 *  counting panels, holes or sheets depending on the row. */
function normaliseSjbProcessing(r: SjbProcessingRaw): SearchHit {
  const unit = String(r.unit ?? "each");
  return {
    code: String(r.code ?? ""),
    desc: `${String(r.desc ?? "")} (${processingUnitLabel(unit)})`,
    listPrice: Number(r.price ?? 0),
    w: 0, h: 0, d: 0,
    meta: {
      // Filed as a sundry on the quote, like the existing SJB drilling line.
      category: "Sundries",
      sjbProcessingUnit: unit,
      ...(r.alreadyNet === true && { sjbAlreadyNet: "1" }),
    },
  };
}

async function runSearch(sel: Selection, query: string, categories?: string[]): Promise<SearchHit[]> {
  const path = resolveEndpoint(sel.supplier, sel.category, query, sel, categories);
  if (!path) return [];
  try {
    const raw = await api<{ results?: unknown[]; total?: number }>(path);
    const results = Array.isArray(raw.results) ? raw.results : [];
    switch (sel.supplier) {
      // TKC migrated onto the PG catalogue 2026-05-24: same wire shape as
      // CUSTOM, so it uses the same normaliser. The toolbar-modals.ts
      // scanner search still hits /api/tkc/search directly with its own
      // local normaliser (separate code path).
      case "TKC":    return results.map((r) => normaliseCustom(r as CustomRaw));
      case "DTN":    return results.map((r) => normaliseDtn(r as DtnRaw));
      case "LDL": {
        let hits = results.map((r) => normaliseLdl(r as LdlRaw));
        // LDL brand filter is client-side per source kos-qb-v3.js:2130.
        if (sel.ldlBrand && sel.ldlBrand !== "All") {
          const wanted = sel.ldlBrand.toLowerCase();
          hits = hits.filter((h) => String(h.meta?.["brand"] ?? "").toLowerCase() === wanted);
        }
        return hits;
      }
      case "SJB":
        return sel.sjbCat === SJB_PILL_PROCESSING
          ? results.map((r) => normaliseSjbProcessing(r as SjbProcessingRaw))
          : results.map((r) => normaliseSjb(r as SjbRaw));
      case "CUSTOM": return results.map((r) => normaliseCustom(r as CustomRaw));
      default: {
        // Generic catalogue-backed supplier (e.g. PWS) — /catalogue/search returns
        // the same wire shape as TKC/CUSTOM, so it shares normaliseCustom.
        const def = supplierTabs.find((s) => s.key === sel.supplier);
        return def && def.catalogueSlug ? results.map((r) => normaliseCustom(r as CustomRaw)) : [];
      }
    }
  } catch (err) {
    console.warn("[qb-products] search failed", err);
    return [];
  }
}

/** Stamp a row with the catalogue that answered, so nothing downstream has to
 *  ask the tab. Rows from /catalogue/search already name their supplier; DTN,
 *  LDL and SJB serve one supplier each, so the tab def IS the row's supplier
 *  and the value stamped here is exactly what the tab would have supplied. */
function stampSource(hit: SearchHit, def: SupplierDef): SearchHit {
  return {
    ...hit,
    sourceTab: def.key,
    supplierSlug: hit.supplierSlug ?? def.catalogueSlug ?? def.key.toLowerCase(),
    supplierName: hit.supplierName ?? def.supplierKey ?? def.label ?? def.key,
  };
}

/**
 * One query, every catalogue the retailer can see.
 *
 * Run in PARALLEL and merged. Measured 2026-09-16: Postgres across TKC+PWS
 * 57-65ms, LDL's 65,206 rows 58-77ms, DTN 3-8ms, SJB in memory. Sequentially
 * that is the sum; in parallel it is the slowest leg, ~80-120ms, and the box
 * already waits 300ms after the last keystroke.
 *
 * A failing catalogue must not empty the list. runSearch swallows its own
 * errors and returns [], so one dead endpoint costs its own rows and nothing
 * else — the alternative, Promise.all rejecting, would make the whole feature
 * only as reliable as its least reliable backend.
 *
 * Pills are NOT carried in. A category or range pill is a browse affordance
 * belonging to one tab; applying SJB's "Corner Base" to a cross-catalogue
 * search would silently filter catalogues that have never heard of it.
 */
async function runAllSearch(sel: Selection, query: string): Promise<SearchHit[]> {
  const q = query.trim();
  if (!q) return [];
  const sources = supplierTabs.filter((d) => !isAllTab(d.key) && !!d.api);
  const perTab = await Promise.all(sources.map(async (def) => {
    // DEFENSIVE, and currently unreachable: the ALL tab renders no chip strip,
    // and switching tabs clears every pill. Kept because it is one change away
    // from mattering — `sjbCat` is ALREADY not cleared on a tab switch, and a
    // pill that survived into a fan-out would silently filter catalogues that
    // have never heard of it. Not claimed as tested; nothing can reach it.
    const scoped: Selection = {
      ...sel,
      supplier: def.key,
      tkcCat: "All", sjbCat: "All", dtnCat: "All", ldlBrand: "All", catRange: "All",
      collection: null,
    };
    const hits = await runSearch(scoped, q);
    return hits.slice(0, PER_SUPPLIER_LIMIT).map((h) => stampSource(h, def));
  }));
  return perTab.flat();
}

export function mountProductsPanel(
  host: HTMLElement,
  store: QuoteStore,
): () => void {
  const sel: Selection = { ...DEFAULT_SELECTION };
  // Let the module-scope endpoint resolver read this quote's SJB defaults.
  sjbDefaultsRef = () => getSjbDefaults(store);
  // Section pills are catalogue-driven — drop any cached set on mount so a
  // newly-uploaded / removed GROUP_NAME is reflected whenever the panel
  // re-mounts (tab switch or navigation), instead of showing stale pills for
  // the whole SPA session. The /tkc-sections response is server-cached (5 min),
  // so the refetch is cheap. Skipped while a fetch is already in flight.
  if (!TKC_SECTIONS_CACHE.loading) TKC_SECTIONS_CACHE.value = null;
  let results: SearchHit[] = [];
  let searching = false;
  let searchTimer: number | null = null;
  let searchSeq = 0;
  // For sections with client-side filter dropdowns: `sectionAllHits` is the
  // full fetched set, `results` is the filtered view actually shown, and
  // `filterSel` holds the selected option label per filter key ("All" = off).
  let sectionAllHits: SearchHit[] = [];
  const filterSel: Record<string, string> = {};
  /** What the active TKC category chip filtered OUT of the last search.
   *
   *  The chip narrows client-side (see scheduleSearch), so the unfiltered set
   *  is already in memory — counting it costs nothing. Without this a chip is
   *  a silent trapdoor: "drawer pack" on the Doors chip looks like the
   *  catalogue has none, when what happened is that three of them are filed
   *  under Drawers. */
  let crossCat: { count: number; cats: string[] } = { count: 0, cats: [] };

  /** Apply the active filter dropdowns for `section` to `hits` (AND across
   *  dropdowns). No-op for sections without filters. */
  function applySectionFilters(section: string, hits: SearchHit[]): SearchHit[] {
    const defs = SECTION_FILTERS[section];
    if (!defs) return hits;
    return hits.filter((h) => {
      const d = (h.desc ?? "").toUpperCase();
      const c = String(h.meta?.["category"] ?? "").toLowerCase();
      for (const f of defs) {
        const cur = filterSel[f.key] ?? "All";
        if (cur === "All") continue;
        const opt = f.options.find((o) => o.label === cur);
        if (opt && !opt.test(d, c)) return false;
      }
      return true;
    });
  }

  /** Filter-dropdown row HTML for `section` (empty string when none). */
  function sectionFiltersHtml(section: string): string {
    const defs = SECTION_FILTERS[section];
    if (!defs) return "";
    const rows = defs.map((f) => {
      const cur = filterSel[f.key] ?? "All";
      const opts = [`<option value="All"${cur === "All" ? " selected" : ""}>All</option>`]
        .concat(f.options.map((o) =>
          `<option value="${escapeHtml(o.label)}"${cur === o.label ? " selected" : ""}>${escapeHtml(o.label)}</option>`))
        .join("");
      return `<label class="qb-products__filter"><span class="qb-products__filter-lbl">${escapeHtml(f.label)}</span><select class="qb-products__filter-sel" data-filter-key="${escapeHtml(f.key)}">${opts}</select></label>`;
    }).join("");
    return `<div class="qb-products__filters-row">${rows}</div>`;
  }
  // Warm the discount cache for whichever supplier the panel opens on so the
  // first render has its trade-price hints + the first addItem has the right
  // discPct. Fire-and-forget — re-render is triggered by the panel's existing
  // render() once data lands (idempotent; entries are cached forever per mount).
  void loadDiscountsFor(sel.supplier);
  // Session-defaults (board finish) strip — mounted ONLY when the left-pane
  // Cabinets chip is active (Steve 2026-05-15). Strip lives above the chip
  // strip; lifecycle is owned by render().
  let sessionStripHandle: SessionDefaultsHandle | null = null;
  // Jigsaw picker (cascade or accessory list) mounted INSIDE this left panel
  // when the TKC > Jigsaw collection is active. The right pane stays quote-only.
  let jigsawHandle: JigsawPickerHandle | null = null;

  // ── W8 mode (DTN tab + Cabinet chip, Phase 1) ────────────────────────────
  //
  // Replaces the flat-search list with the W8 chip-driven cabinet picker
  // experience (15 cabinet chips → /api/dtn/cabinets + DRAWER_PACK_CONFIGS).
  // Card click publishes the selected cab via the selected-cab pubsub; the
  // right-pane picker-area mounts the W8 configurator on receive.
  let w8ChipId: string = W8_CABINET_CHIPS[0]?.id ?? "base";
  let w8Items: W8Cab[] = [];
  let w8Loading = false;
  let w8SearchQ = "";
  let w8Seq = 0;

  function activeW8Chip(): W8Chip | null {
    return W8_CABINET_CHIPS.find((c) => c.id === w8ChipId) ?? null;
  }

  function isW8Mode(): boolean {
    return sel.supplier === "DTN" && sel.dtnCat === "Cabinet";
  }

  async function fetchDtnCabinets(type: string, q: string): Promise<DtnCabRow[]> {
    const qs = new URLSearchParams({ type, limit: "500" });
    if (q) qs.set("q", q);
    try {
      const data = await api<{ results?: DtnCabRow[] }>(`/dtn/cabinets?${qs.toString()}`);
      return Array.isArray(data.results) ? data.results : [];
    } catch { return []; }
  }

  /** Resolve a drawer-pack subcat → real DTN cabs for that subcat. Used
   *  by the Drawer Packs chip render (kos-qb-v3.js:11131–11142). */
  async function fetchDrawerPackCarcasses(): Promise<DtnCabRow[]> {
    const subcats = ["720 Drawer Packs", "3 Drawer Pack", "4 Drawer Pack", "Drawer Packs"];
    const lists = await Promise.all(subcats.map(async (sub) => {
      const rows = await fetchDtnCabinets("base", sub);
      return rows.filter((r) => (r.section || "").indexOf(sub) !== -1);
    }));
    return lists.flat();
  }

  /** Synthesise W8VirtualCab rows from DRAWER_PACK_CONFIGS + a real
   *  carcass per (config, width). Verbatim port of SOT 11145–11206. */
  function buildDrawerPackVirtualCabs(carcasses: DtnCabRow[]): W8VirtualCab[] {
    const out: W8VirtualCab[] = [];
    for (const cfg of DRAWER_PACK_CONFIGS) {
      for (const w of cfg.widths) {
        const subcatMatches = carcasses.filter(
          (it) => cfg.subcats.some((s) => (it.section || "").indexOf(s) !== -1),
        );
        let carcass: DtnCabRow | undefined = subcatMatches.find(
          (it) => "width_mm" in it && (it as unknown as { width_mm?: number }).width_mm === w,
        );
        if (!carcass && subcatMatches.length) {
          carcass = subcatMatches.slice().sort((a, b) => {
            const aw = (a as unknown as { width_mm?: number }).width_mm ?? 0;
            const bw = (b as unknown as { width_mm?: number }).width_mm ?? 0;
            return Math.abs(aw - w) - Math.abs(bw - w);
          })[0];
        }
        if (!carcass && carcasses.length) {
          carcass = carcasses.slice().sort((a, b) => {
            const aw = (a as unknown as { width_mm?: number }).width_mm ?? 0;
            const bw = (b as unknown as { width_mm?: number }).width_mm ?? 0;
            return Math.abs(aw - w) - Math.abs(bw - w);
          })[0];
        }
        const virtualCode = `DP_${cfg.id}_${w}`;
        const pg5 = carcass?.pg5 ?? 0;
        out.push({
          code: virtualCode,
          desc: `${cfg.label} ${w}mm — ${cfg.desc}`,
          unit_type: "drawer_packs",
          unit_label: "Drawer Packs",
          section: cfg.desc,
          width_mm: w,
          height_mm: carcass?.h ?? 720,
          depth_mm: carcass?.d ?? 570,
          h: carcass?.h ?? 720,
          d: carcass?.d ?? 570,
          pg5,
          pg4: carcass?.pg4 ?? pg5,
          pg3: carcass?.pg3 ?? pg5,
          pg2: carcass?.pg2 ?? pg5,
          pg1: carcass?.pg1 ?? pg5,
          _dpConfig: cfg,
          _carcassCode: carcass?.code ?? null,
          _carcassDesc: carcass?.desc ?? null,
          _carcass: carcass ?? null,
        });
      }
    }
    return out;
  }

  async function loadW8List(): Promise<void> {
    const chip = activeW8Chip();
    if (!chip) { w8Items = []; return; }
    w8Loading = true;
    paintW8List();
    const mySeq = ++w8Seq;

    let items: W8Cab[];
    if (chip.kind === "drawer_packs") {
      const carcasses = await fetchDrawerPackCarcasses();
      if (mySeq !== w8Seq) return;
      items = buildDrawerPackVirtualCabs(carcasses);
    } else {
      const rows = await fetchDtnCabinets(chip.type, w8SearchQ);
      if (mySeq !== w8Seq) return;
      let filtered = rows;
      if (chip.section) {
        const kw = chip.section.toUpperCase();
        filtered = filtered.filter((it) => (it.section || "").toUpperCase().indexOf(kw) !== -1);
      }
      items = filtered;
    }

    if (w8SearchQ.trim()) {
      const sq = w8SearchQ.toLowerCase();
      items = items.filter((it) => `${it.desc} ${it.code} ${it.section ?? ""}`.toLowerCase().indexOf(sq) !== -1);
    }
    w8Items = items;
    w8Loading = false;
    paintW8List();
  }

  function paintW8List(): void {
    const box = host.querySelector<HTMLElement>("[data-role='w8-list']");
    if (!box) return;
    if (w8Loading) { box.innerHTML = `<div class="qb-w8-empty">Loading…</div>`; return; }
    if (w8Items.length === 0) { box.innerHTML = `<div class="qb-w8-empty">No items found</div>`; return; }

    // Group by section (verbatim SOT 11247–11254).
    const groups = new Map<string, W8Cab[]>();
    const order: string[] = [];
    for (const it of w8Items) {
      const g = it.section || "General";
      if (!groups.has(g)) { groups.set(g, []); order.push(g); }
      groups.get(g)!.push(it);
    }

    const multipleGroups = order.length > 1;
    const html = order.map((g) => {
      const items = groups.get(g)!;
      const header = multipleGroups
        ? `<div class="qb-w8-grouphead">${escapeHtml(g)} <span class="qb-w8-grouphead-count">${items.length}</span></div>`
        : "";
      const cards = items.map((item) => {
        const priceShown = item.pg5 || 0;
        return `<div class="qb-w8-card" data-code="${escapeHtml(item.code)}">
          <div class="qb-w8-card-info">
            <div class="qb-w8-card-name">${escapeHtml(item.desc)}</div>
            <div class="qb-w8-card-sub"><span class="sku">${escapeHtml(item.code)}</span></div>
          </div>
          <div class="qb-w8-card-price">${priceShown ? `£${priceShown.toFixed(2)}` : "—"}</div>
        </div>`;
      }).join("");
      return header + cards;
    }).join("");

    box.innerHTML = html;

    box.querySelectorAll<HTMLElement>(".qb-w8-card").forEach((card) => {
      const code = card.dataset["code"];
      if (!code) return;
      card.addEventListener("click", () => {
        const cab = w8Items.find((c) => c.code === code);
        if (!cab) return;
        // Bug-1 diagnostic (2026-05-15): a card click is producing a full
        // page reload Steve can't trace. Static analysis ruled out form /
        // anchor / native-submit pathways; the strongest remaining
        // hypothesis is an uncaught error in the pubsub fan-out triggering
        // Vite's dev-mode reload. Wrap in try/catch + console.error so the
        // error surfaces in DevTools before any reload kicks in. Remove
        // after root-cause is fixed.
        try {
          setSelectedCab(cab);
          setActivePicker("cabinets");
          handOffToPicker();
        } catch (err) {
          console.error("[w8 card click]", err);
        }
      });
    });
  }

  /** Tab → PickerSupplier.key resolver. Returns null for tabs without a
   *  cabinet-supplier mapping (none currently — CUSTOM is "Manual" — but
   *  kept as a guard for future tabs). */
  function tabSupplierKey(): string | null {
    switch (sel.supplier) {
      case "TKC":    return "TKC";
      case "DTN":    return "Trade Kitchens by DTN";
      case "LDL":    return "LDL";
      case "CUSTOM": return "Manual";
      default:       return null;
    }
  }

  /** Cabinets-chip predicate per tab. TKC has display category "Cabinets";
   *  DTN's chip strip comes from /api/dtn/categories which uses singular
   *  "Cabinet". LDL has only brand chips and CUSTOM has none — strip never
   *  mounts there. */
  function isCabinetsChipActive(): boolean {
    if (sel.supplier === "TKC") return sel.tkcCat === "Cabinets";
    if (sel.supplier === "DTN") return sel.dtnCat === "Cabinet";
    return false;
  }

  function activeZoneId(): string {
    const q = store.get();
    return q.zones[0]?.id ?? "";
  }

  /** Pull the classifier's inputs off a search hit. `meta` is populated by the
   *  per-supplier normalisers above: catalogue-backed tabs (TKC, PWS, CUSTOM)
   *  carry category + doorRange + doorColour; DTN carries its own category
   *  string; LDL carries only a brand, which the classifier correctly finds
   *  unhelpful and falls through on. */
  function factsFor(hit: SearchHit): CatalogueRowFacts {
    const meta = hit.meta ?? {};
    const str = (k: string): string =>
      typeof meta[k] === "string" ? (meta[k] as string) : "";
    return {
      name: hit.desc,
      category: str("category"),
      doorRange: str("doorRange"),
      colour: str("doorColour"),
    };
  }

  /** Line categorisation for a search hit.
   *
   *  An explicitly-picked TKC chip still wins — that is a deliberate user
   *  choice, not an inference. Otherwise the row's own catalogue fields decide
   *  (classifyCatalogueHit), which replaced inferring from the product NAME on
   *  2026-08-28: a TKC door is named "110 X  596", so the old name regex filed
   *  every one of them under sundries. */
  function itemCategoryFor(hit: SearchHit): ItemCategory {
    if (sel.supplier === "TKC" && sel.tkcCat !== "All") {
      return TKC_CATEGORY_TO_ITEM[sel.tkcCat];
    }
    return classifyCatalogueHit(factsFor(hit));
  }

  /**
   * Hand an SJB cabinet to its picker instead of adding it straight to the
   * quote (2026-09-04).
   *
   * Direct-add committed a line using the quote defaults with no chance to
   * confirm them, and no way to set the hinge side or choose a frontal — the
   * frontal had to be added afterwards as a separate, unlinked door line.
   * The picker owns all of that and commits nothing until Confirm.
   */
  /**
   * Ask before throwing away a part-built configuration.
   *
   * Only when something is actually registered as in progress, and only when
   * the click would REPLACE it — re-opening the same SJB cabinet, or clicking
   * a second door while the door picker is open, costs nothing and must not
   * nag. A confirm that fires when there is nothing to lose is a confirm
   * people learn to click through.
   */
  async function mayReplaceConfigurator(targetKey: string): Promise<boolean> {
    const open = getConfiguratorInProgress();
    if (!open) return true;
    if (targetKey === currentConfiguratorKey) return true;
    return confirmDialog(
      `You are part-way through ${open}. Opening this will discard it.`,
      {
        title: "Discard what you are configuring?",
        confirmLabel: "Discard and open",
        cancelLabel: "Keep configuring",
        // Red: this throws away work and cannot be undone.
        destructive: true,
      },
    );
  }

  /** Identifies what the picker area currently holds, so re-opening the same
   *  thing is not treated as replacing it. */
  let currentConfiguratorKey = "";

  async function openSjbPicker(hit: SearchHit): Promise<void> {
    if (!(await mayReplaceConfigurator(`sjb:${hit.code}`))) return;
    currentConfiguratorKey = `sjb:${hit.code}`;
    setSelectedSjbCode(hit.code);
    setActivePicker("cabinets");
    notifyPickerRemount();
  }

  function addItem(
    partial: Partial<QuoteItem>,
    hint?: { pgCategoryName?: string | null; hit?: SearchHit },
  ): void {
    // The ROW's supplier when the endpoint named one, the tab's otherwise.
    // A "+ Add custom" line has no hit at all and so gets the tab's, which is
    // the only honest answer for something the retailer typed.
    const sup = hitSupplier(hint?.hit, sel.supplier);
    // Discount resolution: explicit > PG range/category override > 0.
    // The PG category name is the catalogue_categories.name string (e.g.
    // "CAMBRIDGE FIR GREEN DOORS"), NOT the canonical ItemCategory ("doors").
    // Callers that have the PG name from a search hit pass it via the hint;
    // otherwise we fall back to using doorRange alone (range-only match).
    //
    // No more supplier defaultDiscPct fallback — Steve 2026-05-25: a discount
    // is shown only when the supplier has explicitly configured one for this
    // retailer (otherwise 0% / trade = list).
    //
    // Keyed on the ROW's supplier: a TKC door found through "My Catalogue"
    // must get TKC's discount, not the one configured against the tab it
    // happened to be found from. The cache is warmed per supplier when the
    // results land (see scheduleSearch), so this is a hit by the time anyone
    // can click; a miss still means "no configured discount", i.e. 0%.
    const hitMeta = hint?.hit?.meta ?? {};
    const resolved = resolveDiscPct(
      sup.slug,
      hint?.pgCategoryName ?? null,
      partial.doorRange ?? null,
      {
        discountCode: typeof hitMeta["discountCode"] === "string" ? hitMeta["discountCode"] : null,
        doorColour: partial.doorColour
          ?? (typeof hitMeta["doorColour"] === "string" ? hitMeta["doorColour"] : null),
        code: partial.code ?? null,
      },
    );
    const discPct = partial.discPct ?? resolved ?? 0;
    // The tab id ('DTN', 'CUSTOM') and the supplier string stored on the
    // line ('Trade Kitchens by DTN', 'Custom') are distinct in source —
    // see kos-qb-v3.js:1668 / 1670 PROD_TABS.supplier. Mirror that here.
    const supplierLabel = sup.label;
    // Search hits pass `category` explicitly (resolved by itemCategoryFor from
    // the row's own catalogue fields). This fallback is for the "+ Add custom"
    // buttons only: an active TKC chip names the category, otherwise
    // sel.category — which is "sundries", the honest answer for a manual line
    // on a tab with no category chip strip.
    const category: ItemCategory = partial.category
      ?? (sel.supplier === "TKC" && sel.tkcCat !== "All"
        ? TKC_CATEGORY_TO_ITEM[sel.tkcCat]
        : sel.category);
    const item: QuoteItem = {
      id: newId("i"),
      category,
      supplier: supplierLabel,
      code: partial.code ?? "",
      desc: partial.desc ?? "Custom item",
      qty: partial.qty ?? 1,
      width: partial.width ?? 0,
      height: partial.height ?? 0,
      depth: partial.depth ?? 0,
      notes: partial.notes ?? "",
      listPrice: partial.listPrice ?? 0,
      discPct,
      tradePrice: 0,
      markupMode: "pct",
      markupPct: store.get().defaults.markupPct ?? 0,
      markupGbp: 0,
      retailPrice: 0,
      ...(partial.doorRange !== undefined && { doorRange: partial.doorRange }),
      ...(partial.doorColour !== undefined && { doorColour: partial.doorColour }),
      ...(partial.unitTypeId !== undefined && { unitTypeId: partial.unitTypeId }),
      ...(partial.unitTypeLabel !== undefined && { unitTypeLabel: partial.unitTypeLabel }),
    };
    recalcItem(item, null);
    const zoneId = activeZoneId();
    store.set((d) => {
      const z = d.zones.find((zo) => zo.id === zoneId) ?? d.zones[0];
      if (z) z.items.push(item);
    });
  }

  /** The current tab's def if it's a generic catalogue (catalogue-backed but
   *  not TKC — e.g. PWS), else undefined. Such tabs use range_family pills. */
  function currentGenericCatalogueDef(): SupplierDef | undefined {
    const def = supplierTabs.find((s) => s.key === sel.supplier);
    return def && def.catalogueSlug && def.key !== "TKC" ? def : undefined;
  }

  /** Resolve the effective search query for the current tab + chip state.
   *  Source kos-qb-v3.js:1898 seeds a per-TKC-category keyword (e.g.
   *  "base unit" for Cabinets) when the user picks a chip without typing.
   *  Returns null when the panel should sit on a hint state. */
  function effectiveQuery(): string | null {
    const typed = sel.query.trim();
    if (typed) return typed;
    if (sel.supplier === "TKC" && sel.tkcCat !== "All") {
      return TKC_CATEGORY_SEARCH_TERMS[sel.tkcCat];
    }
    return null;
  }

  function scheduleSearch(): void {
    if (searchTimer !== null) window.clearTimeout(searchTimer);
    // Generic catalogue (e.g. PWS) with an EMPTY box → door-range cards, no
    // fetch; paintResults renders them and filters client-side.
    //
    // With a typed query it now falls through to the real search (2026-08-28).
    // Before, this short-circuit was unconditional, so PWS could only ever
    // return its 59 range CARDS — the 13,664 individual products behind them
    // were unreachable from the panel however you typed. The server had always
    // answered product-level queries for PWS correctly; the client just never
    // asked. TKC looked fine only because it is exempted from this branch by
    // being keyed "TKC".
    // "All catalogues" searches on a typed term and nothing else: there is no
    // pill to browse and no one catalogue to show the front page of.
    if (isAllTab(sel.supplier)) {
      const q = sel.query.trim();
      if (!q) { results = []; sectionAllHits = []; searching = false; paintResults(); return; }
      searching = true;
      paintResults();
      searchTimer = window.setTimeout(async () => {
        searchTimer = null;
        const mine = ++searchSeq;
        const hits = await runAllSearch(sel, q);
        if (mine !== searchSeq) return;
        for (const slug of new Set(hits.map((h) => h.supplierSlug).filter(Boolean))) {
          void loadDiscountsFor(slug as string);
        }
        results = hits;
        sectionAllHits = hits;
        crossCat = { count: 0, cats: [] };
        searching = false;
        paintResults();
      }, 300);
      return;
    }
    if (currentGenericCatalogueDef() && !sel.query.trim()) {
      searching = false;
      paintResults();
      return;
    }
    // Category-backed TKC sections (Drawers, and any non-Doors section with
    // uploaded GROUP_NAMEs) fetch by category so the full section shows even
    // with no typed query — component rows whose name has no section keyword
    // still appear. A typed query narrows within the category.
    const cats = sel.supplier === "TKC" ? tkcSectionCategories(sel.tkcCat) : [];
    const eff = cats.length > 0 ? sel.query.trim() : effectiveQuery();
    // An SJB pill browses: it fetches its category with an empty box, the
    // same way a TKC section pill does.
    const sjbBrowsing = sel.supplier === "SJB" && sel.sjbCat !== "All";
    if (cats.length === 0 && !eff && !sjbBrowsing) {
      results = [];
      searching = false;
      paintResults();
      return;
    }
    searching = true;
    paintResults();
    searchTimer = window.setTimeout(async () => {
      searchTimer = null;
      const mySeq = ++searchSeq;
      let hits = await runSearch(sel, eff || "", cats);
      if (mySeq !== searchSeq) return;
      // Warm the discount cache for every supplier the results actually came
      // from, not just the tab's. On a single-supplier tab this is the one
      // already loaded on mount and the call is a no-op; on a list that spans
      // catalogues it is what makes the row's own discount available by the
      // time anyone can click it. Same approach as add-item-search.ts.
      for (const slug of new Set(hits.map((h) => h.supplierSlug).filter(Boolean))) {
        void loadDiscountsFor(slug as string);
      }
      // Shared re-rank, one call site for every LH tab. A chip-driven browse
      // has no query to rank against, and rankHits leaves it alone.
      hits = rankHits(sel.supplier, sel.query, hits);
      crossCat = { count: 0, cats: [] };
      if (cats.length > 0) {
        // Already scoped server-side to the section's categories. Apply any
        // client-side filter dropdowns (colour / finish / type) on top.
        sectionAllHits = hits;
        results = applySectionFilters(sel.tkcCat, hits);
      } else if (sel.supplier === "TKC" && sel.tkcCat !== "All") {
        // TKC chip filter — verbatim source kos-qb-v3.js:1903–1909. When a
        // specific chip is picked, drop hits the regex doesn't classify
        // under that category. Fall back to unfiltered when the filter
        // empties the list (so an empty page never blocks the user).
        const filtered = hits.filter((h) => tkcInferCategory(h.desc) === sel.tkcCat);
        results = filtered.length > 0 ? filtered : hits;
        // Record what the chip hid. Only when it actually narrowed — the
        // fallback path above already shows everything, so there is nothing
        // elsewhere to point at.
        if (filtered.length > 0 && hits.length > filtered.length) {
          const others = new Map<string, number>();
          for (const h of hits) {
            const c = tkcInferCategory(h.desc);
            if (c !== sel.tkcCat) others.set(c, (others.get(c) ?? 0) + 1);
          }
          crossCat = {
            count: hits.length - filtered.length,
            cats: [...others.entries()].sort((a, b) => b[1] - a[1]).map(([c]) => c),
          };
        }
      } else {
        results = hits;
      }
      searching = false;
      paintResults();
    }, 300);
  }

  /** One result row. Extracted so the flat list and the grouped
   *  "All catalogues" list render an identical row rather than two that merely
   *  look alike — `data-i` is the index into `results`, which is how the click
   *  handler finds the hit again, so both renderers must slice before they
   *  index and neither may reorder. */
  function hitRowHtml(r: SearchHit, i: number): string {
    {
        // Render trade price when a PG discount applies for this category/range,
        // otherwise just the list price. Format per Steve 2026-05-24:
        // "Trade: £X.XX (45% off list)".
        const meta = r.meta ?? {};
        const categoryName = (meta["category"] as string | undefined) ?? null;
        const doorRange    = (meta["doorRange"] as string | undefined)
                          ?? (meta["range"]     as string | undefined)
                          ?? null;
        // The ROW's supplier. On a single-supplier tab this is the tab's, so
        // nothing moves; in a mixed list it stops every row being priced
        // against whichever catalogue the tab happens to name.
        const discPct = resolveDiscPct(hitSupplier(r, sel.supplier).slug, categoryName, doorRange, {
          discountCode: typeof meta["discountCode"] === "string" ? meta["discountCode"] : null,
          doorColour: typeof meta["doorColour"] === "string" ? meta["doorColour"] : null,
          code: r.code,
        });
        // Show the SAME text the quote line will get. A TKC door's catalogue
        // name is its size, so an un-composed card list reads as a column of
        // bare "110 X 596" with nothing to tell two ranges apart until after
        // you have clicked one.
        const cardDesc = composeCatalogueDesc(factsFor(r));
        const priceHtml = discPct != null && discPct > 0
          ? `<div class="qb-product-hit__price qb-product-hit__price--discounted">
               <div class="qb-product-hit__trade">Trade: £${(r.listPrice * (1 - discPct / 100)).toFixed(2)}</div>
               <div class="qb-product-hit__discnote">${discPct.toFixed(2).replace(/\.?0+$/, "")}% off list</div>
             </div>`
          : `<div class="qb-product-hit__price">£${r.listPrice.toFixed(2)}</div>`;
        return `
          <li class="qb-product-hit" data-i="${i}" tabindex="0" role="button" aria-label="Configure ${escapeHtml(cardDesc)}" title="Click to configure · use + Add for quick-add">
            <div class="qb-product-hit__main">
              <div class="qb-product-hit__desc">${escapeHtml(cardDesc)}</div>
              <div class="qb-product-hit__meta">
                <span class="qb-product-hit__code">${escapeHtml(r.code)}</span>
                ${r.w || r.h || r.d ? `<span class="qb-product-hit__dim">${r.w || "-"}×${r.h || "-"}×${r.d || "-"}mm</span>` : ""}
              </div>
            </div>
            ${priceHtml}
            <button type="button" class="qb-product-hit__add" data-role="card-add" title="Quick-add to quote">+ Add</button>
          </li>`;
    }
  }

  /** Render a flat unordered list of results. Used by non-TKC tabs and by
   *  the TKC tab when a specific chip is active (the chip itself is the
   *  group header). */
  function renderHitList(hits: SearchHit[]): string {
    if (isAllTab(sel.supplier)) return renderGroupedHitList(hits);
    return `<ul class="qb-products__results-list">${
      hits.slice(0, FLAT_RESULT_CAP).map((r, i) => hitRowHtml(r, i)).join("")}</ul>`;
  }

  /**
   * The same rows, under a heading per supplier.
   *
   * Only for "All catalogues", where the list spans catalogues whose prices,
   * lead times and ordering are all different — an undifferentiated run of
   * rows would invite comparing two numbers that are not comparable. Groups
   * follow the tab order rather than relevance so the list reads the same way
   * twice, and each carries its own count because a supplier's rows are capped
   * (PER_SUPPLIER_LIMIT) and a truncated group must say so.
   */
  function renderGroupedHitList(hits: SearchHit[]): string {
    const shown = hits.slice(0, FLAT_RESULT_CAP);
    const groups = new Map<string, Array<{ r: SearchHit; i: number }>>();
    shown.forEach((r, i) => {
      const k = r.sourceTab ?? sel.supplier;
      if (!groups.has(k)) groups.set(k, []);
      groups.get(k)!.push({ r, i });
    });
    const parts: string[] = [];
    for (const def of supplierTabs) {
      const rows = groups.get(def.key);
      if (!rows || rows.length === 0) continue;
      // The same header the range and section lists use, tinted. Reused rather
      // than reinvented so one change of mind moves all of them.
      parts.push(`<li class="qb-products__group-header qb-products__group-header--supplier"
            role="presentation" style="--qb-supplier-colour:${def.colour}"
            data-group="${escapeHtml(def.key)}">
          ${escapeHtml(def.label ?? def.key)}
          <span class="qb-products__group-count">${rows.length}${
            rows.length >= PER_SUPPLIER_LIMIT ? "+" : ""}</span>
        </li>`);
      for (const { r, i } of rows) parts.push(hitRowHtml(r, i));
    }
    return `<ul class="qb-products__results-list">${parts.join("")}</ul>`;
  }

  /** Click / Enter on a range card opens the door configurator pre-seeded.
   *  Shared by the browse view and the typed-query view so the two cannot
   *  diverge in what a card does. */
  function wireRangeCards(box: HTMLElement, cards: readonly RangeCard[]): void {
    box.querySelectorAll<HTMLElement>("li.qb-range-card").forEach((li) => {
      const c = cards[Number(li.dataset["cardIdx"])];
      if (!c) return;
      const open = (): void => {
        // Seed with stockType so the door picker opens in the right mode.
        // Stock seeds the first stock colour as a pre-fill (legacy behaviour);
        // PTO/Signature/Bespoke leave the colour blank so the user picks /
        // enters it inside the configurator after locking in a size.
        const seedColour = c.tier === "stock"
          ? (c.range.coloursByType?.stock[0] ?? c.range.colours[0] ?? "")
          : "";
        setPickerSeed({
          range: c.range.name, finish: seedColour,
          supplier: "TKC", stockType: c.tier,
        });
        setActivePicker("doors");
        notifyPickerRemount();
        handOffToPicker();
      };
      li.addEventListener("click", open);
      li.addEventListener("keydown", (e) => {
        const ke = e as KeyboardEvent;
        if (ke.key === "Enter" || ke.key === " ") { ke.preventDefault(); open(); }
      });
    });
  }

  /** Range cards matching the typed query, to sit ABOVE the product results.
   *
   *  Only on the TKC tab, and only with something typed. On the Doors chip
   *  this answers "which range?"; on All it means a range name still finds
   *  its configurator instead of only its individual doors. */
  function typedRangeCards(): RangeCard[] {
    if (sel.supplier !== "TKC") return [];
    const q = sel.query.trim();
    if (!q) return [];
    if (sel.tkcCat !== "Doors" && sel.tkcCat !== "All") return [];
    const ranges = TKC_RANGES_CACHE.value;
    if (!ranges) {
      // Not fetched yet — the browse view is normally what warms this cache,
      // and a retailer who types immediately never opened it. Kick it off and
      // repaint when it lands rather than silently showing no ranges.
      void loadTkcRanges(paintResults);
      return [];
    }
    return matchRangeCards(q, buildRangeCards(ranges));
  }

  /** "3 more matches in Drawers — Search all categories". Rendered under the
   *  results whenever the active chip hid something the query did match. */
  function crossCategoryNoteHtml(): string {
    if (sel.supplier !== "TKC" || sel.tkcCat === "All" || crossCat.count === 0) return "";
    const named = crossCat.cats.slice(0, 3).map(escapeHtml).join(", ");
    const more = crossCat.cats.length > 3 ? ` and ${crossCat.cats.length - 3} more` : "";
    return `
      <div class="qb-products__crosscat" data-role="cross-cat-note">
        <span>${crossCat.count} more match${crossCat.count === 1 ? "" : "es"} in ${named}${more}.</span>
        <button type="button" class="qb-products__crosscat-btn" data-role="cross-cat">Search all categories</button>
      </div>`;
  }

  function paintResults(): void {
    const box = host.querySelector<HTMLElement>("[data-role='results']");
    if (!box) return;

    // Generic catalogue (e.g. PWS) with an EMPTY search box → door-range cards.
    // Cards come from /catalogue/door-ranges?supplier=<slug>, filtered by the
    // active range-family pill (sel.catRange, prefix match). Clicking a card
    // scopes the shared door picker to this supplier and opens it pre-seeded.
    // PWS is stock-only, so one card per range (the picker handles colour +
    // size + accessories).
    //
    // Typing now switches to flat product results below, matching TKC's
    // "Doors chip + no query → cards, query → products" behaviour (2026-08-28).
    // The cards stay the browse affordance; the search box is no longer a
    // card filter.
    const genDef = currentGenericCatalogueDef();
    if (genDef && genDef.catalogueSlug && !sel.query.trim()) {
      const slug = genDef.catalogueSlug;
      const supLabel = genDef.supplierKey ?? genDef.key;
      const entry = catRangesEntry(slug);
      if (entry.value === null) {
        box.innerHTML = `<div class="qb-products__hint">Loading ${escapeHtml(supLabel)} door ranges…</div>`;
        void loadCatalogueRanges(slug, paintResults);
        return;
      }
      // Range-pill filter only — the typed-query filter that used to live here
      // is gone, because this branch is now only reached with an empty box.
      const famSel = sel.catRange !== "All" ? sel.catRange.toLowerCase() : "";
      const ranges = famSel
        ? entry.value.filter((r) => r.name.toLowerCase().startsWith(famSel))
        : entry.value;
      if (ranges.length === 0) {
        box.innerHTML = `<div class="qb-products__hint">No ${escapeHtml(supLabel)} door ranges in ${escapeHtml(sel.catRange)}. Pick another range above, or search for a product by name, code or size.</div>`;
        return;
      }
      box.innerHTML = `
        <div class="qb-products__group-header">Doors <span class="qb-products__group-count">(${ranges.length} range${ranges.length === 1 ? "" : "s"})</span></div>
        <ul class="qb-products__results-list qb-products__range-cards">
          ${ranges.map((r, i) => {
            const colourCount = r.coloursByType?.stock.length ?? r.colours.length;
            return `
            <li class="qb-product-hit qb-range-card" data-card-idx="${i}" tabindex="0" role="button" aria-label="Configure ${escapeHtml(r.name)}">
              <div class="qb-product-hit__main">
                <div class="qb-product-hit__desc">${escapeHtml(r.name)}</div>
                <div class="qb-product-hit__meta">
                  <span class="qb-product-hit__code">Stocked colours</span>
                  ${colourCount ? `<span class="qb-product-hit__dim">${colourCount} colour${colourCount === 1 ? "" : "s"}</span>` : ""}
                </div>
              </div>
              <div class="qb-product-hit__price">Pick</div>
            </li>`;
          }).join("")}
        </ul>
      `;
      box.querySelectorAll<HTMLElement>("li.qb-range-card").forEach((li) => {
        const idx = Number(li.dataset["cardIdx"]);
        const r = ranges[idx];
        if (!r) return;
        const open = (): void => {
          const seedColour = r.coloursByType?.stock[0] ?? r.colours[0] ?? "";
          // Scope the shared door picker to this supplier, then seed + open it.
          setDoorSupplier({ slug, label: supLabel });
          setPickerSeed({ range: r.name, finish: seedColour, supplier: supLabel, stockType: "stock" });
          setActivePicker("doors");
          notifyPickerRemount();
          handOffToPicker();
        };
        li.addEventListener("click", open);
        li.addEventListener("keydown", (e) => {
          const ke = e as KeyboardEvent;
          if (ke.key === "Enter" || ke.key === " ") { ke.preventDefault(); open(); }
        });
      });
      return;
    }

    // TKC + 'Doors' + no typed query → render door range cards (verbatim
    // source 1816-1847). Click a range card → opens door picker pre-filled.
    if (sel.supplier === "TKC" && sel.tkcCat === "Doors" && !sel.query.trim()) {
      const ranges = TKC_RANGES_CACHE.value;
      if (!ranges) {
        box.innerHTML = `<div class="qb-products__hint">Loading TKC door ranges…</div>`;
        void loadTkcRanges(paintResults);
        return;
      }
      if (ranges.length === 0) {
        box.innerHTML = `<div class="qb-products__hint">No TKC door ranges available. Try the search box above.</div>`;
        return;
      }
      const cards = buildRangeCards(ranges);
      box.innerHTML =
        `<div class="qb-products__group-header">Doors <span class="qb-products__group-count">(${cards.length} option${cards.length === 1 ? "" : "s"})</span></div>`
        + renderRangeCardList(cards);
      wireRangeCards(box, cards);
      return;
    }

    // TKC + 'All' + no typed query: pick a category from the chip strip
    // above, or use the search box. The legacy "Browse by category" button
    // grid (kos-qb-v3.js:1873–1881) was removed 2026-05-26 — duplicated the
    // chips and added visual noise.
    if (sel.supplier === "TKC" && sel.tkcCat === "All" && !sel.query.trim()) {
      box.innerHTML = `
        <div class="qb-products__hint">Select a category above (e.g. Doors), or use the search box to find any TKC product by name, SKU, or description.</div>
      `;
      return;
    }

    // Manual is no longer a tab — verbatim source has TKC / DTN / LDL /
    // My Catalogue. The "+ Add custom" affordance still surfaces below as
    // a fallback for no-endpoint and empty-result paths.
    const endpointKnown = isAllTab(sel.supplier)
      || resolveEndpoint(sel.supplier, sel.category, "x") !== null;
    if (!endpointKnown && sel.supplier !== "TKC") {
      box.innerHTML = `
        <div class="qb-products__hint">
          No live search for ${escapeHtml(sel.supplier)} → ${escapeHtml(sel.category)} yet.
          Use the door picker above (for doors) or add a custom line.
        </div>
        <button type="button" class="qb-btn qb-btn--ghost" data-role="add-custom">
          + Add custom ${escapeHtml(sel.category)} item
        </button>
      `;
      box.querySelector<HTMLButtonElement>("[data-role='add-custom']")?.addEventListener("click", () => {
        addItem({ desc: sel.query.trim() || `Custom ${sel.category} item`, qty: 1 });
        sel.query = "";
        const inp = host.querySelector<HTMLInputElement>("[data-role='search']");
        if (inp) inp.value = "";
      });
      return;
    }

    // Non-TKC tabs with an empty query — show "type to search" hint. (Generic
    // catalogues like PWS never reach here — they return above via the
    // door-range-card view.)
    if (sel.supplier !== "TKC" && !sel.query.trim()
        && !(sel.supplier === "SJB" && sel.sjbCat !== "All")) {
      box.innerHTML = isAllTab(sel.supplier)
        ? `<div class="qb-products__hint">
             Type to search every catalogue you have at once — ${
               escapeHtml(supplierTabs.filter((d) => !isAllTab(d.key) && !!d.api)
                 .map((d) => d.label ?? d.key).join(", "))}.
             Results are grouped by supplier.
           </div>`
        : `<div class="qb-products__hint">
             Type to search the ${escapeHtml(sel.supplier)} catalogue${
               sel.supplier === "SJB" ? ", or pick a category above" : ""}.
           </div>`;
      return;
    }

    if (searching) {
      box.innerHTML = `<div class="qb-products__hint">Searching…</div>`;
      return;
    }

    // Range cards for the typed query, rendered ABOVE the product results.
    // Computed here — before the no-results branch — because the case that
    // matters most is exactly the one with no products: the server's LIKE
    // matcher returns nothing for "cartmell", while the range it means is
    // sitting right there.
    const rangeCards = typedRangeCards();
    const rangeCardsHtml = rangeCards.length
      ? `<div class="qb-products__group-header">Ranges <span class="qb-products__group-count">(${rangeCards.length})</span></div>`
        + renderRangeCardList(rangeCards)
      : "";

    // Filtered sections handle their own empty state below (keeping the filter
    // dropdowns visible so the user can clear a filter that emptied the list).
    if (results.length === 0 && !(sel.supplier === "TKC" && SECTION_FILTERS[sel.tkcCat])) {
      const label = sel.supplier === "TKC" ? sel.tkcCat : sel.category;
      const queryShown = sel.query.trim() || (sel.supplier === "TKC" ? sel.tkcCat : "");
      // A range match is a real answer, so do not lead with "no matches" when
      // one is on screen — say what DID match and offer the custom line under
      // it rather than above it.
      box.innerHTML = rangeCardsHtml + `
        <div class="qb-products__hint">
          ${rangeCardsHtml
            ? `No individual products match &quot;${escapeHtml(queryShown)}&quot; — pick a range above to configure one.`
            : `No matches for &quot;${escapeHtml(queryShown)}&quot;. Try a wider term, or add a custom line.`}
        </div>
        <button type="button" class="qb-btn qb-btn--ghost" data-role="add-custom">
          + Add custom ${escapeHtml(label)} item
        </button>
      `;
      wireRangeCards(box, rangeCards);
      box.querySelector<HTMLButtonElement>("[data-role='add-custom']")?.addEventListener("click", () => {
        addItem({ desc: sel.query.trim() || `Custom ${label} item`, qty: 1 });
      });
      return;
    }

    // TKC + query + 'All': group results by inferred TKC category with
    // section headers (verbatim source kos-qb-v3.js:1914 group header).
    if (sel.supplier === "TKC" && sel.tkcCat === "All" && sel.query.trim()) {
      const bucketed = new Map<TkcDisplayCategory, SearchHit[]>();
      for (const r of results) {
        const cat = tkcInferCategory(r.desc);
        const arr = bucketed.get(cat) ?? [];
        arr.push(r);
        bucketed.set(cat, arr);
      }
      const sections = TKC_CATEGORY_ORDER
        .filter((c) => bucketed.has(c))
        .map((c) => `
          <div class="qb-products__group-header">${escapeHtml(c)} <span class="qb-products__group-count">(${bucketed.get(c)!.length})</span></div>
          ${renderHitList(bucketed.get(c)!)}
        `)
        .join("");
      box.innerHTML = rangeCardsHtml + sections;
    } else if (sel.supplier === "TKC") {
      // TKC + specific chip → single section, chip-labelled header, optionally
      // preceded by client-side filter dropdowns (Quick Cabs / Handles / Vero).
      const filtersHtml = sectionFiltersHtml(sel.tkcCat);
      if (filtersHtml && results.length === 0) {
        box.innerHTML = rangeCardsHtml + filtersHtml + `<div class="qb-products__hint">No items match the selected filters.</div>`;
      } else {
        const header = sel.tkcCat !== "All"
          ? `<div class="qb-products__group-header">${escapeHtml(sel.tkcCat)} <span class="qb-products__group-count">(${results.length})</span></div>`
          : "";
        box.innerHTML = rangeCardsHtml + filtersHtml + header + renderHitList(results)
          + crossCategoryNoteHtml();
      }
    } else {
      // An active pill sitting next to results from other categories needs a
      // word, or it reads as a bug. Says what happened and offers the way
      // back to browsing.
      const pillIgnored = sel.supplier === "SJB" && sel.sjbCat !== "All" && !!sel.query.trim();
      const scopeNote = pillIgnored
        ? `<div class="qb-products__crosscat" data-role="sjb-scope-note">
             <span>Searching the whole SJB catalogue, not just ${escapeHtml(sel.sjbCat)}.</span>
             <button type="button" class="qb-products__crosscat-btn" data-role="sjb-clear-query">Back to ${escapeHtml(sel.sjbCat)}</button>
           </div>`
        : "";
      box.innerHTML = scopeNote + renderHitList(results);
    }
    box.querySelector<HTMLButtonElement>("[data-role='sjb-clear-query']")?.addEventListener("click", () => {
      // Clear the QUERY, not the pill — the retailer asked to go back to the
      // category they were browsing.
      sel.query = "";
      const inp = host.querySelector<HTMLInputElement>("[data-role='search']");
      if (inp) inp.value = "";
      render();
      scheduleSearch();
    });
    wireRangeCards(box, rangeCards);
    box.querySelector<HTMLButtonElement>("[data-role='cross-cat']")?.addEventListener("click", () => {
      // Widen to All and keep the query — the whole point is to see the hits
      // the chip filtered out, without retyping.
      sel.tkcCat = "All";
      render();
      scheduleSearch();
    });
    // Card-body click → open picker pre-filled. Card "+ Add" button →
    // direct-add. Verbatim source: card body "click any item to
    // configure it here" (line 2364) + "+Add" button direct-add (line 1751).
    for (const li of box.querySelectorAll<HTMLElement>("li.qb-product-hit")) {
      const i = Number(li.dataset["i"]);
      const hit = results[i];
      if (!hit) continue;
      // Which catalogue THIS row came from. On a single-supplier tab it is the
      // active tab; in the "All catalogues" list every row answers for itself,
      // and routing on the tab would send a TKC door to SJB's picker.
      const srcTab = hit.sourceTab ?? sel.supplier;
      const srcDef = supplierTabs.find((d) => d.key === srcTab);
      const directAdd = (): void => {
        // SJB lines are priced server-side against the line's own colour and
        // then split into carcass + hardware, so they cannot be built from a
        // SearchHit's single listPrice. Re-price authoritatively and let
        // sjb-catalogue.ts assemble the line; that is the same call the
        // per-line editor makes, so the two can never drift.
        // "+ Add" is a quick-add everywhere else, but an SJB cabinet has
        // choices that change its price and cannot be defaulted silently, so
        // it opens the picker like a row click does.
        if (srcTab === "SJB" && sel.sjbCat !== SJB_PILL_PROCESSING) { void openSjbPicker(hit); return; }
        const partial: Partial<QuoteItem> = {
          code: hit.code,
          // Composed, not raw (2026-08-28). A TKC door's catalogue name is its
          // SIZE — "110 X  596" — so the raw name read as nothing on the quote.
          // composeCatalogueDesc prefixes range + colour for TKC and leaves
          // PWS (whose range already carries the finish) alone. Rows with no
          // doorRange pass through unchanged.
          desc: composeCatalogueDesc(factsFor(hit)),
          category: itemCategoryFor(hit),
          listPrice: hit.listPrice,
          width: hit.w,
          height: hit.h,
          depth: hit.d,
          qty: 1,
        };
        let pgCategoryName: string | null = null;
        if (hit.meta) {
          if (typeof hit.meta["doorRange"] === "string") partial.doorRange = hit.meta["doorRange"];
          if (typeof hit.meta["doorColour"] === "string") partial.doorColour = hit.meta["doorColour"];
          if (typeof hit.meta["unitTypeId"] === "string") partial.unitTypeId = hit.meta["unitTypeId"];
          if (typeof hit.meta["unitTypeLabel"] === "string") partial.unitTypeLabel = hit.meta["unitTypeLabel"];
          if (typeof hit.meta["category"] === "string") pgCategoryName = hit.meta["category"];
        }
        addItem(partial, { pgCategoryName, hit });
      };
      const configure = async (): Promise<void> => {
        // SJB rows always configure — never direct-add. See openSjbPicker.
        if (srcTab === "SJB" && sel.sjbCat !== SJB_PILL_PROCESSING) { void openSjbPicker(hit); return; }
        // Canonical picker category, resolved from the row's own catalogue
        // fields (an explicitly-picked TKC chip still wins inside).
        const cat = itemCategoryFor(hit);
        // Ask before discarding a part-built configuration. Keyed on the
        // picker that would open, not on the row, so clicking a second door
        // into an already-open door picker does not prompt.
        if (!(await mayReplaceConfigurator(`${srcTab}:${cat}`))) return;
        currentConfiguratorKey = `${srcTab}:${cat}`;

        // ── Pickers this supplier cannot actually mount ───────────────────
        //
        // Two pickers need catalogue-backed state the tab may not have, and
        // routing to them anyway is what produced the "click does nothing"
        // reports: the right pane just showed an empty hint and the only way
        // to get the item onto the quote was "+ Add", skipping configuration.
        //
        //   cabinets — needs a selected W8 cab, only ever published by the DTN
        //              Cabinet chip's card list (setSelectedCab). A search hit
        //              never carries one, so picker-area falls straight to its
        //              empty state.
        //   doors    — the door picker fetches /catalogue/door-ranges?supplier=
        //              <slug>. DTN and LDL have no `suppliers` row at all, so
        //              it would open with nothing in it.
        //
        // DTN cabinets get a real route instead — see below. Everything else
        // falls back to a direct add, which is honest: we cannot configure it,
        // so we at least put it on the quote correctly categorised.
        // Read off the ROW's catalogue: a TKC door found through "All
        // catalogues" can open the door picker, and before this it could not,
        // because the pseudo-tab has no slug of its own.
        const def = srcDef;
        const canMountDoors = !!def?.catalogueSlug;

        if (cat === "cabinets") {
          // Gate the W8 hand-off on DTN's OWN category string, not on the
          // classified ItemCategory. 11 DTN rows filed under "Accessory" —
          // Blum blind-corner hinges, Antaro Space Towers — classify as
          // cabinets via the name fallback ("blind corner", "tower"), and
          // yanking the retailer into the Cabinet chip because they clicked a
          // hinge would be worse than the bug this replaces. Verified against
          // all 1,775 rows of the DTN CSV: category==="Cabinet" is exactly the
          // 456 real cabinets.
          if (sel.supplier === "DTN" && hit.meta?.["category"] === "Cabinet") {
            // DTN keeps its cabinets in a SECOND, disjoint catalogue: the
            // search box reads trade_kitchens_dtn_catalogue.csv (codes like
            // "1000HL") while the cabinet picker reads data/dtn/dtn-cabinets.csv
            // (codes like "2024B150"). Zero code overlap, and their price-group
            // columns run in opposite directions — so this exact row cannot be
            // handed to the picker. Send the retailer to the flow where DTN
            // cabinets ARE configurable instead, carrying their query across so
            // they land on the equivalent unit rather than an empty list.
            // (Steve's call, 2026-08-28.)
            w8SearchQ = sel.query.trim();
            sel.dtnCat = "Cabinet";
            render();
            return;
          }
          directAdd();
          return;
        }
        if (cat === "doors" && !canMountDoors) {
          directAdd();
          return;
        }

        // Build a seed from the hit; door / cabinet pickers consume the
        // relevant fields. Other pickers ignore unknowns.
        const seed: Record<string, unknown> = {
          sku: hit.code,
          desc: hit.desc,
          listPrice: hit.listPrice,
          width: hit.w,
          height: hit.h,
          depth: hit.d,
        };
        if (hit.meta?.["doorRange"])  seed["range"]  = hit.meta["doorRange"];
        if (hit.meta?.["doorColour"]) seed["finish"] = hit.meta["doorColour"];
        // Label the seed with the ROW's supplier string, falling back to the
        // tab's. Generic catalogues (PWS, future imports) resolve from the
        // registry rather than being listed here, so a newly-granted catalogue
        // seeds correctly without a code change.
        const seedSupplier = hitSupplier(hit, sel.supplier).label;
        if (seedSupplier) seed["supplier"] = seedSupplier;
        setPickerSeed(seed);
        setActivePicker(cat);
        notifyPickerRemount();
        handOffToPicker();
      };

      // "+ Add" button (direct-add) — stop propagation to the card body.
      const addBtn = li.querySelector<HTMLButtonElement>("[data-role='card-add']");
      if (addBtn) {
        addBtn.addEventListener("click", (e) => {
          e.stopPropagation();
          directAdd();
        });
      }
      li.addEventListener("click", () => { void configure(); });
      li.addEventListener("keydown", (e) => {
        const ke = e as KeyboardEvent;
        if (ke.key === "Enter" || ke.key === " ") {
          ke.preventDefault();
          void configure();
        }
      });
    }

    // Section filter dropdowns (Quick Cabs colour / Handles finish+type / Vero
    // finish) — re-filter the cached full set in place; no re-fetch.
    box.querySelectorAll<HTMLSelectElement>("[data-filter-key]").forEach((selEl) => {
      selEl.addEventListener("change", () => {
        const k = selEl.dataset["filterKey"];
        if (k) filterSel[k] = selEl.value;
        results = applySectionFilters(sel.tkcCat, sectionAllHits);
        paintResults();
      });
    });
  }

  function render(): void {
    const supplierPills = supplierTabs.map((s) => {
      const cls = `qb-supplier-pill ${s.key === sel.supplier ? "is-active" : ""}`;
      const display = s.label ?? s.key;
      const title = s.note ? ` title="${escapeHtml(s.note)}"` : "";
      return `<button type="button" class="${cls}" data-supplier="${escapeHtml(s.key)}" style="--qb-supplier-colour:${s.colour}"${title}>${escapeHtml(display)}</button>`;
    }).join("");

    // Each supplier has its own chip strip — verbatim source:
    //   • TKC    → TKC_CATEGORY_ORDER (11 static display categories), source 1762
    //   • DTN    → /api/dtn/categories (async), source 1925-1949
    //   • LDL    → /api/ldl/brands (top 12, async), source 2086-2102
    //   • CUSTOM → no chip strip (search-only)
    const isTkc = sel.supplier === "TKC";
    const isDtn = sel.supplier === "DTN";
    const isLdl = sel.supplier === "LDL";
    const isCustom = sel.supplier === "CUSTOM";
    const isSjb = sel.supplier === "SJB";
    let catChips = "";
    if (isTkc) {
      // Unified pill nav (Steve 2026-05-31) — replaces the old hardcoded
      // category chip strip. Reads "‹ TKC catalogue | Doors | Jigsaw | Modus":
      //   • a home chip (back to the catalogue landing) when a section or
      //     collection is open,
      //   • one amber collection-style pill per POPULATED off-the-shelf section
      //     (Doors, Worktops, …) from /catalogue/tkc-sections — nothing is
      //     pre-installed, a section only appears once its CSVs are uploaded,
      //   • the Jigsaw / Modus collection pills.
      // Off-the-shelf sections and collections are siblings in one row, all in
      // the qb-chip--collection style.
      const inSection = !!sel.collection || sel.tkcCat !== "All";
      const homeChip = inSection
        ? `<button type="button" class="qb-chip" data-tkc-home="1">‹ TKC catalogue</button>`
        : "";
      const sections = TKC_SECTIONS_CACHE.value;
      let sectionPills: string;
      if (sections === null) {
        sectionPills = `<span class="qb-products__hint">Loading…</span>`;
        void loadTkcSections(render);
      } else {
        sectionPills = sections.map(({ section: c }) =>
          `<button type="button" class="qb-chip qb-chip--collection ${!sel.collection && c === sel.tkcCat ? "is-active" : ""}" data-tkc-cat="${escapeHtml(c)}">${escapeHtml(c)}</button>`,
        ).join("");
      }
      const collectionPills = COLLECTION_KEYS.map((k) =>
        `<button type="button" class="qb-chip qb-chip--collection ${sel.collection === k ? "is-active" : ""}" data-collection="${escapeHtml(k)}">${escapeHtml(COLLECTIONS[k].label)}</button>`,
      ).join("");
      catChips = homeChip + sectionPills + collectionPills;
    } else if (isDtn) {
      const cats = DTN_CATS_CACHE.value;
      if (cats) {
        const list = ["All", ...cats];
        catChips = list.map((c) =>
          `<button type="button" class="qb-chip ${c === sel.dtnCat ? "is-active" : ""}" data-dtn-cat="${escapeHtml(c)}">${escapeHtml(c)}</button>`,
        ).join("");
      } else {
        catChips = `<span class="qb-products__hint">Loading DTN categories…</span>`;
        void loadDtnCats(render);
      }
    } else if (isSjb) {
      const cats = SJB_CATS_CACHE.value;
      if (cats) {
        // Count in the label: 95 Larders and 4 Curved units are very
        // different propositions, and the pill that opens on nothing is
        // never offered because the server counts before it is rendered.
        catChips = [
          `<button type="button" class="qb-chip ${sel.sjbCat === "All" ? "is-active" : ""}" data-sjb-cat="All">All</button>`,
          // Set apart from the recipe categories: these two are a picker and
          // a different catalogue, not a slice of the cabinet list.
          `<button type="button" class="qb-chip qb-chip--alt ${sel.sjbCat === SJB_PILL_PANEL ? "is-active" : ""}" data-sjb-cat="${escapeHtml(SJB_PILL_PANEL)}">${escapeHtml(SJB_PILL_PANEL)}</button>`,
          `<button type="button" class="qb-chip qb-chip--alt ${sel.sjbCat === SJB_PILL_PROCESSING ? "is-active" : ""}" data-sjb-cat="${escapeHtml(SJB_PILL_PROCESSING)}">${escapeHtml(SJB_PILL_PROCESSING)}</button>`,
        ]
          .concat(cats.filter((c) => c.count > 0).map((c) =>
            `<button type="button" class="qb-chip ${c.name === sel.sjbCat ? "is-active" : ""}" data-sjb-cat="${escapeHtml(c.name)}">${escapeHtml(c.name)} <span class="qb-chip__count">${c.count}</span></button>`))
          .join("");
      } else {
        catChips = `<span class="qb-products__hint">Loading SJB categories…</span>`;
        void loadSjbCats(render);
      }
    } else if (isLdl) {
      const brands = LDL_BRANDS_CACHE.value;
      if (brands) {
        const list = ["All", ...brands.slice(0, 12)];
        catChips = list.map((b) =>
          `<button type="button" class="qb-chip ${b === sel.ldlBrand ? "is-active" : ""}" data-ldl-brand="${escapeHtml(b)}">${escapeHtml(b)}</button>`,
        ).join("");
      } else {
        catChips = `<span class="qb-products__hint">Loading LDL brands…</span>`;
        void loadLdlBrands(render);
      }
    } else if (isCustom) {
      catChips = `<span class="qb-products__hint">Search your private uploaded catalogues</span>`;
    } else {
      const catDef = currentGenericCatalogueDef();
      if (catDef && catDef.catalogueSlug) {
        // Generic catalogue (e.g. PWS) — range pills from /catalogue/sections.
        // An "All" pill (search-only) leads; one pill per range_family follows.
        // No range data → no pills (search-only), never the misleading
        // ITEM_CATEGORIES strip.
        const entry = catSectionsEntry(catDef.catalogueSlug);
        if (entry.value === null) {
          catChips = `<span class="qb-products__hint">Loading ranges…</span>`;
          void loadCatalogueSections(catDef.catalogueSlug, render);
        } else if (entry.value.length === 0) {
          catChips = "";
        } else {
          const allPill = `<button type="button" class="qb-chip ${sel.catRange === "All" ? "is-active" : ""}" data-cat-range="All">All</button>`;
          const rangePills = entry.value.map((s) =>
            `<button type="button" class="qb-chip ${s.name === sel.catRange ? "is-active" : ""}" data-cat-range="${escapeHtml(s.name)}">${escapeHtml(s.name)}</button>`,
          ).join("");
          catChips = allPill + rangePills;
        }
      } else {
        catChips = ITEM_CATEGORIES.map(
          (c) => `<button type="button" class="qb-chip ${c === sel.category ? "is-active" : ""}" data-category="${escapeHtml(c)}">${escapeHtml(c)}</button>`,
        ).join("");
      }
    }

    // Collection mode swap — TKC + a collection (Jigsaw/Modus). The whole
    // picker (cascade or accessory list) mounts HERE in the left panel; the
    // right pane is cleared to quote-only via setActivePicker(null). Placed
    // before the canonical setActivePicker below so it isn't overridden.
    if (isTkc && sel.collection) {
      const def = COLLECTIONS[sel.collection];
      // Keep the sub-cat valid for this collection (Modus has fewer chips).
      if (!def.subCats.includes(sel.collectionCat)) sel.collectionCat = def.subCats[0] ?? "Cabinets";
      const subChips = def.subCats.map((c) =>
        `<button type="button" class="qb-chip ${c === sel.collectionCat ? "is-active" : ""}" data-collection-cat="${escapeHtml(c)}">${escapeHtml(c)}</button>`,
      ).join("");

      host.innerHTML = `
        <div class="qb-products">
          <div class="qb-products__suppliers" role="tablist" aria-label="Supplier">
            ${supplierPills}
          </div>

          <div class="qb-products__cats" role="tablist" aria-label="TKC catalogue">
            ${catChips}
          </div>

          <div class="qb-products__cats" role="tablist" aria-label="${escapeHtml(def.label)} category">
            ${subChips}
          </div>

          <div class="qb-products__results" data-role="collection-host"></div>
        </div>
      `;

      if (sessionStripHandle) { sessionStripHandle.unmount(); sessionStripHandle = null; }

      // Right pane shows no picker for the collection flow — quote only.
      setActivePicker(null);

      // Mount the picker into the left panel: Cabinets → cascade, else list.
      const collHost = host.querySelector<HTMLElement>("[data-role='collection-host']");
      if (jigsawHandle) { jigsawHandle.unmount(); jigsawHandle = null; }
      if (collHost) {
        jigsawHandle = sel.collectionCat === "Cabinets"
          ? mountJigsawCabinetPicker(collHost, store, def.cfg)
          : mountJigsawAccessoryPicker(collHost, store, sel.collectionCat, def.cfg);
      }

      host.querySelectorAll<HTMLButtonElement>("[data-supplier]").forEach((btn) => {
        btn.addEventListener("click", () => {
          if (btn.disabled) return;
          sel.supplier = btn.dataset.supplier ?? sel.supplier;
          sel.query = "";
          sel.tkcCat = "All";
          sel.collection = null;
          sel.dtnCat = "All";
          sel.ldlBrand = "All";
          sel.catRange = "All";
          results = [];
          render();
        });
      });
      host.querySelector<HTMLButtonElement>("[data-tkc-home]")?.addEventListener("click", () => {
        sel.collection = null;
        sel.tkcCat = "All";
        sel.query = "";
        results = [];
        render();
      });
      host.querySelectorAll<HTMLButtonElement>("[data-collection]").forEach((btn) => {
        btn.addEventListener("click", () => {
          sel.collection = (btn.dataset["collection"] as CollectionKey) ?? sel.collection;
          render();
        });
      });
      // Picking an off-the-shelf section pill while inside a collection exits
      // the collection and drills into that section.
      host.querySelectorAll<HTMLButtonElement>("[data-tkc-cat]").forEach((btn) => {
        btn.addEventListener("click", () => {
          const c = btn.dataset["tkcCat"] as TkcChip | undefined;
          if (!c) return;
          sel.tkcCat = c;
          sel.collection = null;
          sel.query = "";
          results = [];
          render();
          scheduleSearch();
        });
      });
      host.querySelectorAll<HTMLButtonElement>("[data-collection-cat]").forEach((btn) => {
        btn.addEventListener("click", () => {
          sel.collectionCat = (btn.dataset["collectionCat"] as JigsawSubCat) ?? sel.collectionCat;
          render();
        });
      });

      return;
    }

    // Not in collection mode — tear down any mounted collection picker so it
    // doesn't leak when we fall through to the normal render.
    if (jigsawHandle) { jigsawHandle.unmount(); jigsawHandle = null; }

    // The pickers all live in the RIGHT pane (source kos-qb-v3.js:1303
    // `renderPicker` slot). Left pane writes to picker-selection on chip
    // click; the right-pane picker-area subscribes and re-mounts.
    // Flat-list TKC categories (Handles, Sinks & Taps, etc.) render their
    // products in the left results list only — suppress the right-pane picker
    // so it doesn't open empty (Steve 2026-05-31).
    const tkcFlatList =
      isTkc && sel.tkcCat !== "All" && TKC_FLATLIST_CATEGORIES.has(sel.tkcCat);
    const genCatDef = currentGenericCatalogueDef();
    // Scope the shared door picker to the active supplier so a door-card click
    // (or a seedless Doors mount) configures the right catalogue. Generic
    // catalogues (PWS) → their slug/label; everything else → TKC (the picker's
    // default), leaving TKC behaviour unchanged.
    if (genCatDef && genCatDef.catalogueSlug) {
      setDoorSupplier({ slug: genCatDef.catalogueSlug, label: genCatDef.supplierKey ?? genCatDef.key });
    } else {
      setDoorSupplier({ slug: "tkc", label: "TKC" });
    }
    // Which picker the right pane opens on, before anything is clicked.
    //
    // Only the TKC tab can answer this from tab state, because only it has a
    // chip strip that names an ItemCategory. Every other tab mounts NOTHING
    // and waits for a hit click to say what the row actually is (2026-08-28).
    // Previously the non-TKC branch read `sel.category`, which no live tab
    // ever writes — so DTN and LDL opened the cabinet picker on arrival, which
    // then refused to mount without a selected cab and left a dead pane.
    const activeCanonical: ItemCategory | null = tkcFlatList
      ? null
      : genCatDef
        ? null
        : isTkc
          ? (sel.tkcCat === "All" ? null : TKC_CATEGORY_TO_ITEM[sel.tkcCat])
          : null;
    setActivePicker(activeCanonical);

    // Placeholder text. The generic-catalogue tab searches PRODUCTS now, not
    // range cards, so it no longer advertises itself as "<supplier> door
    // ranges" — that wording is what made the card-only behaviour look
    // intentional rather than like the missing search it was.
    const searchLabel = isTkc
      ? (sel.tkcCat === "All" ? "TKC catalogue" : `TKC ${sel.tkcCat}`)
      : genCatDef
        ? `${genCatDef.supplierKey ?? sel.supplier} — name, code, range or size`
        : `the ${sel.supplier} catalogue`;

    // W8 mode swap — DTN + Cabinet chip replaces the standard chip strip
    // / search / flat-results trio with the W8 cabinet chip strip + W8
    // grouped card list. Other tabs/chips fall through to the original
    // markup below.
    if (isW8Mode()) {
      const w8Chips = W8_CABINET_CHIPS.map((c) => {
        const on = c.id === w8ChipId;
        return `<button type="button" class="qb-w8-cat ${on ? "active" : ""}" data-w8-chip="${escapeHtml(c.id)}">${escapeHtml(c.label)}</button>`;
      }).join("");

      host.innerHTML = `
        <div class="qb-products">
          <div class="qb-products__suppliers" role="tablist" aria-label="Supplier">
            ${supplierPills}
          </div>

          <div data-role="session-defaults-host"></div>

          <div class="qb-products__cats" role="tablist" aria-label="DTN tab">
            ${catChips}
          </div>

          <div class="qb-w8-cats" role="tablist" aria-label="Cabinet type">
            ${w8Chips}
          </div>

          <div class="qb-w8-search">
            <input
              type="search"
              placeholder="Search DTN cabinets…"
              value="${escapeHtml(w8SearchQ)}"
              data-role="w8-search"
              autocomplete="off"
            />
          </div>

          <div class="qb-w8-list" data-role="w8-list"><div class="qb-w8-empty">Loading…</div></div>
        </div>
      `;

      if (sessionStripHandle) { sessionStripHandle.unmount(); sessionStripHandle = null; }
      const slot = host.querySelector<HTMLElement>("[data-role='session-defaults-host']");
      const supplierKey = tabSupplierKey();
      if (supplierKey && slot) {
        sessionStripHandle = mountSessionDefaults(slot, store, supplierKey);
      }

      host.querySelectorAll<HTMLButtonElement>("[data-supplier]").forEach((btn) => {
        btn.addEventListener("click", () => {
          if (btn.disabled) return;
          sel.supplier = btn.dataset.supplier ?? sel.supplier;
          sel.query = "";
          sel.tkcCat = "All";
          sel.dtnCat = "All";
          sel.ldlBrand = "All";
          sel.catRange = "All";
          results = [];
          w8SearchQ = "";
          // Warm the discount cache for this supplier so the trade-price
          // hint + addItem discPct lookup can fire synchronously thereafter.
          void loadDiscountsFor(sel.supplier).then(() => render());
          render();
        });
      });
      host.querySelectorAll<HTMLButtonElement>("[data-dtn-cat]").forEach((btn) => {
        btn.addEventListener("click", () => {
          sel.dtnCat = btn.dataset["dtnCat"] ?? "All";
          sel.query = "";
          results = [];
          w8SearchQ = "";
          render();
          if (!isW8Mode()) scheduleSearch();
        });
      });
      host.querySelectorAll<HTMLButtonElement>("[data-w8-chip]").forEach((btn) => {
        btn.addEventListener("click", () => {
          w8ChipId = btn.dataset["w8Chip"] ?? w8ChipId;
          w8SearchQ = "";
          render();
        });
      });
      const w8SearchInput = host.querySelector<HTMLInputElement>("[data-role='w8-search']");
      w8SearchInput?.addEventListener("input", (e) => {
        w8SearchQ = (e.target as HTMLInputElement).value;
        void loadW8List();
      });

      void loadW8List();
      // Drive picker activation now so the right pane shows the empty
      // hint for the W8 picker even before a card is clicked.
      setActivePicker("cabinets");
      return;
    }

    host.innerHTML = `
      <div class="qb-products">
        <div class="qb-products__suppliers" role="tablist" aria-label="Supplier">
          ${supplierPills}
        </div>

        <div data-role="session-defaults-host"></div>

        <div class="qb-products__cats" role="tablist" aria-label="Category">
          ${catChips}
        </div>

        <div class="qb-products__search">
          <input
            type="search"
            placeholder="Search ${escapeHtml(searchLabel)}…"
            value="${escapeHtml(sel.query)}"
            data-role="search"
            autocomplete="off"
          />
        </div>

        <div class="qb-products__results" data-role="results"></div>
      </div>
    `;

    // Session-defaults strip — re-mount fresh on every render. unmount()
    // tears down the previous handle (closure flag prevents late paints)
    // before we mount into the new host slot.
    if (sessionStripHandle) {
      sessionStripHandle.unmount();
      sessionStripHandle = null;
    }
    // SJB gets its own strip: board colour PLUS hinge finish and drawer system,
    // persisted on the quote rather than in localStorage. It is always shown on
    // the SJB tab (not gated on a cabinets chip) because every SJB row is a
    // cabinet and nothing on the tab can be priced until a colour is chosen.
    if (sel.supplier === "SJB") {
      const slot = host.querySelector<HTMLElement>("[data-role='session-defaults-host']");
      if (slot) sessionStripHandle = mountSjbDefaults(slot, store);
    } else if (isCabinetsChipActive()) {
      const supplierKey = tabSupplierKey();
      const slot = host.querySelector<HTMLElement>("[data-role='session-defaults-host']");
      if (supplierKey && slot) {
        sessionStripHandle = mountSessionDefaults(slot, store, supplierKey);
      }
    }

    host.querySelectorAll<HTMLButtonElement>("[data-supplier]").forEach((btn) => {
      btn.addEventListener("click", () => {
        if (btn.disabled) return;
        sel.supplier = btn.dataset.supplier ?? sel.supplier;
        sel.query = "";
        sel.tkcCat = "All";
        sel.collection = null;
        sel.dtnCat = "All";
        sel.ldlBrand = "All";
        // Reset the generic-catalogue range pill too. Its W8-mode twin below
        // already did; this one did not, so a PWS range pill survived the tab
        // switch and silently filtered the next catalogue's results by a
        // range_family it does not have.
        sel.catRange = "All";
        results = [];
        render();
      });
    });
    // Collection entry (Jigsaw/Modus) — swap the TKC tab into collection mode.
    host.querySelectorAll<HTMLButtonElement>("[data-collection]").forEach((btn) => {
      btn.addEventListener("click", () => {
        sel.collection = (btn.dataset["collection"] as CollectionKey) ?? sel.collection;
        sel.query = "";
        results = [];
        render();
      });
    });
    host.querySelectorAll<HTMLButtonElement>("[data-category]").forEach((btn) => {
      btn.addEventListener("click", () => {
        sel.category = (btn.dataset.category as ItemCategory) ?? sel.category;
        sel.query = "";
        results = [];
        render();
      });
    });
    // Generic-catalogue range-family pill (e.g. PWS) — filters the door-range
    // cards below. render() re-paints the cards via paintResults.
    host.querySelectorAll<HTMLButtonElement>("[data-cat-range]").forEach((btn) => {
      btn.addEventListener("click", () => {
        sel.catRange = btn.dataset["catRange"] ?? "All";
        sel.query = "";
        results = [];
        render();
      });
    });
    host.querySelector<HTMLButtonElement>("[data-tkc-home]")?.addEventListener("click", () => {
      sel.collection = null;
      sel.tkcCat = "All";
      sel.query = "";
      results = [];
      render();
    });
    host.querySelectorAll<HTMLButtonElement>("[data-sjb-cat]").forEach((btn) => {
      btn.addEventListener("click", () => {
        sel.sjbCat = btn.dataset["sjbCat"] ?? "All";
        // The panel pill is a picker, not a search. It leaves the pill
        // active so the retailer can see where they are, but fetches
        // nothing — resolveEndpoint returns null for it.
        if (sel.sjbCat === SJB_PILL_PANEL) {
          results = [];
          render();
          setSjbPanelOpen(true);
          setActivePicker("panels");
          notifyPickerRemount();
          return;
        }
        // Keep any typed term — a pill narrows WITHIN the search rather than
        // replacing it, the same as the TKC section pills.
        render();
        scheduleSearch();
      });
    });
    host.querySelectorAll<HTMLButtonElement>("[data-tkc-cat]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const c = btn.dataset["tkcCat"] as TkcChip | undefined;
        if (!c) return;
        sel.tkcCat = c;
        sel.collection = null;
        sel.query = "";
        results = [];
        render();
        scheduleSearch();
      });
    });
    host.querySelectorAll<HTMLButtonElement>("[data-dtn-cat]").forEach((btn) => {
      btn.addEventListener("click", () => {
        sel.dtnCat = btn.dataset["dtnCat"] ?? "All";
        sel.query = "";
        results = [];
        render();
        scheduleSearch();
      });
    });
    host.querySelectorAll<HTMLButtonElement>("[data-ldl-brand]").forEach((btn) => {
      btn.addEventListener("click", () => {
        sel.ldlBrand = btn.dataset["ldlBrand"] ?? "All";
        sel.query = "";
        results = [];
        render();
        scheduleSearch();
      });
    });
    const searchInput = host.querySelector<HTMLInputElement>("[data-role='search']");
    searchInput?.addEventListener("input", (e) => {
      sel.query = (e.target as HTMLInputElement).value;
      scheduleSearch();
    });

    paintResults();
  }

  render();
  // Load the tenant's GRANTED catalogue tabs, then re-render. If the default
  // supplier (TKC) isn't granted, fall back to the first available tab so the
  // panel never opens on an empty/invalid tab.
  void loadCatalogueTabs().then(() => {
    if (!supplierTabs.some((s) => s.key === sel.supplier)) {
      sel.supplier = supplierTabs[0]?.key ?? "CUSTOM";
      sel.query = "";
      sel.tkcCat = "All";
      sel.collection = null;
    }
    render();
  });
  return () => {
    if (searchTimer !== null) window.clearTimeout(searchTimer);
    if (sessionStripHandle) { sessionStripHandle.unmount(); sessionStripHandle = null; }
    if (jigsawHandle) { jigsawHandle.unmount(); jigsawHandle = null; }
  };
}
